import GOOGLE_DOMAINS_DATA_JSON from './google-domains'
import GOOGLE_LENS_COUNTRIES_JSON from './google-lens-countries'
import PROMPTS from './prompts'
import type { CountryCurrencyMapping } from './types'

export const DUPE_TOKEN_ADDRESS = 'fRfKGCriduzDwSudCwpL7ySCEiboNuryhZDVJtr1a1C'
export const DUPE_TOKEN_ADDRESS_DEVNET =
  'Dy3ediRtnESHXi7iKhH4xV2dU4RguHVretBQZEsPm84j'

export const DUPE_TOKEN_ADDRESSES = {
  mainnet: DUPE_TOKEN_ADDRESS,
  devnet: DUPE_TOKEN_ADDRESS_DEVNET,
}

/** Base URL for the proxy worker (proxies external URLs with SSRF protection, edge caching, and optional image transforms). */
export const PROXY_BASE_URL = 'https://proxy.dupe.com'

/**
 * Response header the proxy worker stamps on every proxied response, naming
 * which egress leg served it. Producer: `apps/proxy-worker/src/index.ts`
 * (sets `X-Proxy-Via`). Consumer: `probeImageAccess`
 * (`apps/api/src/lib/helpers.ts`), which treats a ScraperAPI-served probe as
 * NOT raw-accessible (DUP-2077). ⚠️ A value drift between producer and
 * consumer fails OPEN — the probe would read `proxy-direct` and silently
 * reintroduce the DUP-2077 bug. The worker bundles standalone and keeps its
 * own literal, pinned by its tests asserting this exact string; the API side
 * must reference these constants.
 */
export const PROXY_VIA_HEADER = 'x-proxy-via'
/** `X-Proxy-Via` value meaning the ScraperAPI fallback (third-party egress) served the response. */
export const PROXY_VIA_SCRAPER_API = 'scraper-api'

/**
 * Image-fetch proxy for hosts that refuse our other fetchers (see
 * `IMAGEKIT_BLOCKED_HOSTS`). Since DUP-1949 this hostname is a route on our
 * own Cloudflare proxy worker (`apps/proxy-worker`) — blocked-host fetches
 * ride its ScraperAPI fallback (non-Cloudflare egress), which requires
 * `SCRAPER_API_KEY` on the worker env. Kept as a hostname separate from
 * `PROXY_BASE_URL` because stored product data embeds it. History (the
 * BunnyCDN pull-zone era, why the detour existed and why it ended): see
 * `docs/proxy-workers.md`.
 */
export const IMAGE_FETCH_PROXY_BASE_URL = 'https://img-proxy.dupe.com'

/**
 * Hosts that refuse ImageKit's fetcher, so an `assets.dupe.com` URL built
 * against them hangs and the image renders blank.
 *
 * Provenance — DUP-1820, 2026-07-28: ASOS fronts `images.asos-media.com` with
 * Akamai bot protection that rejects ImageKit and all Cloudflare egress. The
 * same URL returns 200 through `IMAGE_FETCH_PROXY_BASE_URL`, times out through
 * `assets.dupe.com`, and 520s through `PROXY_BASE_URL`. Left every ASOS product
 * (196,308 rows) with a blank tile. Catalog ingestion already fetches these
 * images through the same proxy, so this only aligns rendering with ingest.
 *
 * Exact hostname match, deliberately — a suffix match would also capture
 * lookalikes such as `images.asos-media.com.example.com`.
 *
 * Supersedes `docs/references/connexity-catalog-audit/02-findings.md` § F3,
 * which reported "80/80 403s via img-proxy.dupe.com" and concluded the
 * reconstructed URLs were dead. Re-probed 2026-07-28: img-proxy returns 200
 * with real JPEG bytes, and a live browser loads the same URLs at 1026x1309.
 * The audit's failures were client-fingerprint blocking of its own fetchers.
 * Dead slots do exist (the `-1-<color>` variant 404s for ~146k feed rows) but
 * that is a feed-reconstruction bug tracked on DUP-1800, not this one.
 */
export const IMAGEKIT_BLOCKED_HOSTS: ReadonlySet<string> = new Set([
  'images.asos-media.com',
])

/**
 * The origin to hand ImageKit: `url` itself, or the image-fetch-proxy detour
 * when `url`'s host is blocked. Caller is responsible for encoding it.
 *
 * Prefer `buildImageKitUrl` — it does this and the encoding for you. Reach for
 * this ONLY when the URL shape it produces doesn't fit, i.e. ImageKit's
 * path-prefix transform form (`assets.dupe.com/tr:.../<encoded>`).
 */
export function imageKitFetchableOrigin(url: string): string {
  return isImageKitBlockedHost(url)
    ? `${IMAGE_FETCH_PROXY_BASE_URL}/${encodeURIComponent(url)}`
    : url
}

/** True when ImageKit cannot fetch `url`'s origin. False for malformed input. */
export function isImageKitBlockedHost(url: string): boolean {
  try {
    return IMAGEKIT_BLOCKED_HOSTS.has(new URL(url).hostname)
  } catch {
    return false
  }
}

/**
 * Splits the remainder of a concatenated `assets.dupe.com/<origin>` URL into
 * the origin and the transform WE appended. Ours arrives as `?tr=`, or `&tr=`
 * when the origin already carries a query, so split on the LAST one — that
 * keeps the origin's own query intact.
 *
 * Shared so every reader of this stored shape splits it identically.
 */
export function splitStoredImageKitOrigin(rest: string): {
  origin: string
  tr?: string
} {
  const trAt = Math.max(rest.lastIndexOf('?tr='), rest.lastIndexOf('&tr='))
  if (trAt === -1) return { origin: rest }
  return { origin: rest.slice(0, trAt), tr: rest.slice(trAt + 4) }
}

/**
 * Repairs a STORED ImageKit URL at render time. `proxyImageWithImageKit` emits
 * the concatenated `assets.dupe.com/https://origin/path` form — the shape its
 * persisted readers require — but that URL hangs when the origin is blocked.
 * Call this wherever such a value is handed to an `<img>`/`background-image`.
 *
 * No-op for everything else: unblocked origins, media-library paths, and
 * non-ImageKit URLs all come back byte-identical. Any `?tr=` already on the URL
 * is preserved, so transforms are unchanged.
 */
export function repairImageKitUrl(url: string): string
export function repairImageKitUrl(
  url: string | null | undefined
): string | undefined
export function repairImageKitUrl(
  url: string | null | undefined
): string | undefined {
  if (url == null) return undefined
  if (!url.startsWith(IMAGEKIT_URL_ENDPOINT)) return url
  const rest = url.slice(IMAGEKIT_URL_ENDPOINT.length)
  if (!rest.startsWith('http://') && !rest.startsWith('https://')) return url
  const { origin, tr } = splitStoredImageKitOrigin(rest)
  if (!isImageKitBlockedHost(origin)) return url
  return buildImageKitUrl(origin, tr)
}

/**
 * Builds an `assets.dupe.com` (ImageKit) URL, detouring blocked origins through
 * the image-fetch proxy. `tr` is an ImageKit transform list, e.g. `w-600,c-at_max`.
 *
 * Use this for EVERY ImageKit URL whose output is rendered or probed and then
 * thrown away — it is the one place the blocked-host rule lives, so new call
 * sites are covered without having to know the rule exists.
 *
 * ⚠️ Not for URLs that get PERSISTED. The detour changes the shape from the
 * concatenated `assets.dupe.com/https://…` form to a percent-encoded one, and
 * four readers key on the literal `://`: `isPermanentlyUploaded` +
 * `unwrapProxyUrl` (researchProductImagesRehost), `unwrapImageKitProxy`
 * (internal-mcp), `normalizeImageUrl` (researcher control plane). Feeding them
 * the encoded form makes rehosting skip the image forever and breaks dedupe.
 * `proxyImageWithImageKit` emits the storage shape and stays separate.
 *
 * The full `encodeURIComponent` is load-bearing: ImageKit decodes the path once,
 * so a raw concatenation leaks the origin's query string onto the proxy.
 * Verified — raw 404s, encoded 200s.
 */
export function buildImageKitUrl(url: string, tr?: string): string {
  const built = `${IMAGEKIT_URL_ENDPOINT}${encodeURIComponent(
    imageKitFetchableOrigin(url)
  )}`
  return tr ? `${built}?tr=${tr}` : built
}

/**
 * Prepends an ImageKit path transform while preserving the legacy URL shape
 * for every origin ImageKit can already fetch. This is used where a query
 * transform is not suitable (for example iOS notification attachments).
 *
 * Only blocked origins change shape: they are repaired through the image-fetch
 * proxy and encoded so their query string cannot leak onto the ImageKit URL.
 */
export function prependImageKitPathTransform(
  url: string,
  transform: string
): string {
  if (url.startsWith(IMAGEKIT_URL_ENDPOINT)) {
    const rest = url.slice(IMAGEKIT_URL_ENDPOINT.length)
    const { origin, tr } = splitStoredImageKitOrigin(rest)
    if (isImageKitBlockedHost(origin)) {
      const repaired = buildImageKitUrl(origin)
      // ImageKit does not let a later `f-*` override an earlier one. Retain
      // resize/crop operations, but remove the stored format so the requested
      // format is the only one and cannot be silently ignored.
      const retainedTransform = tr
        ?.split(',')
        .filter((operation) => !operation.startsWith('f-'))
        .join(',')
      const combinedTransform = retainedTransform
        ? `${retainedTransform},${transform}`
        : transform
      return `${IMAGEKIT_URL_ENDPOINT}tr:${combinedTransform}/${repaired.slice(
        IMAGEKIT_URL_ENDPOINT.length
      )}`
    }
    return `${IMAGEKIT_URL_ENDPOINT}tr:${transform}/${rest}`
  }

  const origin = isImageKitBlockedHost(url)
    ? encodeURIComponent(imageKitFetchableOrigin(url))
    : url
  return `${IMAGEKIT_URL_ENDPOINT}tr:${transform}/${origin}`
}

// IMPORTANT: This parameter name must stay in sync with mobile app.
// Changing this will break authentication for existing mobile clients that haven't updated.
export const WEB_AUTH_TOKEN_PARAM = 'session-auth'

export const GOOGLE_DOMAINS_DATA = GOOGLE_DOMAINS_DATA_JSON
export const GOOGLE_LENS_COUNTRIES = GOOGLE_LENS_COUNTRIES_JSON

// SerpAPI format https://www.google.com/search?ipb.. SearchAPI format https://www.google.com/search?q=...
// Legacy constant - only matches US google.com
export const GOOGLE_SEARCH_LINK_PREFIX = 'https://www.google.com/search?'

// Regex to match Google search URLs from any country domain (e.g., google.de, google.co.uk, google.com.au)
// Pattern breakdown:
//   - (?:[a-z]{2,3}\.)? - optional country prefix (e.g., "co." or "com.")
//   - [a-z]{2,6} - final TLD (2-6 letters covers standard TLDs like com, de, uk, info)
// This prevents subdomain attacks like google.attacker.com or google.com.attacker.com
export const GOOGLE_SEARCH_URL_REGEX =
  /^https:\/\/(?:www\.)?google\.(?:[a-z]{2,3}\.)?[a-z]{2,6}\/search\?/i

/**
 * Check if a URL is a Google search URL (from any country domain)
 * Handles: google.com, google.de, google.co.uk, google.com.au, etc.
 */
export function isGoogleSearchUrl(url: string | undefined | null): boolean {
  if (!url) return false
  return GOOGLE_SEARCH_URL_REGEX.test(url)
}

/**
 * Broader check for any Google intermediary/shopping URL that should not be
 * surfaced as a direct retailer link.
 *
 * Catches:
 * - google.com/search?...      (standard search redirect)
 * - google.com/shopping/...    (shopping product pages)
 * - shopping.google.com/...    (shopping subdomain)
 * - google.com/url?...         (redirect wrapper)
 */
export const GOOGLE_INTERMEDIARY_URL_REGEX =
  /^https?:\/\/(?:www\.)?google\.(?:[a-z]{2,3}\.)?[a-z]{2,6}\/(?:search\?|shopping\/|url\?)/i

/** Matches shopping.google.* subdomain (any path is intermediary) */
export const GOOGLE_SHOPPING_SUBDOMAIN_REGEX =
  /^https?:\/\/shopping\.google\.(?:[a-z]{2,3}\.)?[a-z]{2,6}\//i

export function isGoogleIntermediaryUrl(
  url: string | undefined | null
): boolean {
  if (!url) return false
  return (
    GOOGLE_INTERMEDIARY_URL_REGEX.test(url) ||
    GOOGLE_SHOPPING_SUBDOMAIN_REGEX.test(url)
  )
}

export const GOOGLE_FAVICON_URL_DEFAULT = 'https://www.google.com/favicon.ico'

// Research defaults (web + api should send explicit counts when available)
export const DEFAULT_RESEARCH_COUNT = 4
export const ONBOARDING_FREEFORM_THRESHOLD = 3
export const ONBOARDING_FREEFORM_MAX_LENGTH = 500
export const MAX_ONBOARDING_QUESTION_COUNT = 7

// Max length of the raw search query persisted into `link_clicks.metadata.q`
// (and surfaced as the `q` URL param on redirect links). Anything longer is
// almost always a copy-paste accident (full URLs, descriptions) that bloats
// metadata jsonb and contaminates aggregations rather than helps ranking
// attribution. Shared between the web redirect-URL builders and the API
// redirect controller so client-side and server-side truncation can never
// drift — when they do, the URL contains a value the DB never stored.
export const MAX_REDIRECT_QUERY_LENGTH = 200

export enum DupeActivityEventName {
  DesktopExtensionInstall = 'desktop_extension_install',
  SafariExtensionInstall = 'safari_extension_install',
  MobileAppInstall = 'mobile_app_install',
  InviteFriend = 'invite_friend',
  PerformDupe = 'perform_dupe',
  ClickResult = 'click_result',
}

// Test user IDs for development
export const INTERNAL_USER_IDS = [
  'kjMJ9O8CMKOirIz0bvFFHaojGzj2', // rusty
  '7zST2OoYH6ONvQlHRlsTzytfwvj2', // bobby
  '1Qq3yRUq1TbuLOYN4fjOyCB6ugN2', // ramin
  'xs1bNWuYsGQEaXOfpqxhYSRuKeC2', // ramin
  'nvVWZdvcnThbjUe4ubCiZYlQozF3', // crystal
  'FoUtBQ7adoPt5fzcHRgmb1pzfR02', // bobby
  'PCSsJt1DnqXh3CdphhVFAyM5ceY2', // ramin
  'kN4QjCqzARSP953sT3W3REyXQDr2', // bobby
]

/**
 * Source tag set in `research_tasks.metadata.source` for tasks launched via
 * the internal MCP planning launcher (`/api/internal/research/plans/launch`).
 * Used to:
 *   - Filter MCP-launched tasks out of community/admin lists so they don't
 *     pollute user-facing surfaces.
 *   - Gate the auto-publish Inngest function so only MCP-attributed tasks
 *     bypass the manual /admin/seo-reports review.
 *
 * Tasks are attributed to the real OAuth-authenticated user who launched them
 * (looked up by X-MCP-User-Email header on the internal API). No synthetic
 * user row is used — the source tag alone identifies MCP-origin tasks.
 */
export const MCP_SOURCE_TAG = 'internal-mcp'

/**
 * @deprecated Kept for backward compatibility with tasks launched before the
 * OAuth-driven user-attribution refactor. Gated flows should use
 * `MCP_SOURCE_TAG` + `metadata.source` checks instead of filtering on user_id.
 * Safe to remove once no tasks with this user_id remain in the system.
 */
export const SYSTEM_INTERNAL_MCP_USER_ID = 'system-internal-mcp'

export const CLASSIFICATIONS_TO_EXCLUDE = [
  'person',
  'human',
  'woman',
  'man',
  'girl',
  'boy',
  'child',
  'children',
  'infant',
  'baby',
  'people',
  'window',
  'animal',
  'vehicle',
  'plant',
  'potted plant',
  'object',
  'book',
  'cup',
  'bottle',
  'cat',
  'dog',
  'bird',
  'fish',
  'hamster',
  'mouse',
  'horse',
  'pet',
  'sky',
  'cloud',
  'house',
  'building',
  'door',
  'wall',
  'floor',
  'ceiling',
  'roof',
  'human face',
  'curtain',
  'flower',
  'tree',
  'tableware',
  'button',
  'ring',
  'shelf',
  'penguin',
  'panda',
  'elephant',
  'giraffe',
  'zebra',
  'lion',
  'car tire',
  'tyre',
  'tire',
  'wheel',
  'tag',
  'label',
  'antenna',
  'mobile phone',
  'candle',
  'drawer',
  'coffee cup',
  'bowl',
  'plate',
  'cup',
  'glass',
  'bottle',
  'jar',
  'bowl',
  'window',
  'door',
  'wall',
  'floor',
  'ceiling',
  'roof',
  'apple',
  'banana',
  'orange',
  'pear',
  'pineapple',
  'strawberry',
  'watermelon',
  'cushion',
]

// Room Types for Restyling
// Process: Add a room name here, make sure prompts.ts is updated with the negative tokens for that room. room-analysis.ts will use this list.
export const ROOM_TYPES = [
  'living-room',
  'bedroom',
  'dining-room',
  'kitchen',
  'bathroom',
  'foyer',
  'entryway',
  'office',
  'patio',
  'home-theater',
  'playroom',
  'laundry-room',
  'sunroom',
  'home-gym',
  'hallway',
  'library',
  'walk-in-closet',
]

export const OPEN_PHONE_PRO_NUMBER = '+17602847414'

export const DUPE_FACTORY_STORE_KEY = 'https://store.dupe.com'

export const DEFAULT_OG_IMAGE = 'https://dupe.com/assets/dupe-og-v1.png'

export const FIREFOX_ADDON_URL = `https://addons.mozilla.org/en-US/firefox/addon/dupe/`
export const DESKTOP_CHROME_WEB_STORE_URL =
  'https://chromewebstore.google.com/detail/dupe/pkhpcfoaocmmpmphhenoblggeinedlah'

/**
 * DUP-2087 — the listing's REVIEWS tab, which is where a review prompt has to
 * land. Derived from the detail URL so the extension id lives in one place.
 *
 * ⚠️ Opening this is `review_intent`, never `reviewed`. Chrome gives no callback
 * telling us a review was actually posted, so anything downstream must be named
 * for the click, not the outcome.
 */
export const DESKTOP_CHROME_WEB_STORE_REVIEW_URL = `${DESKTOP_CHROME_WEB_STORE_URL}/reviews`

// New ios app Pre-order/App store url
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/dupe-com/id6752661840'

// ChatGPT Dupe integration URL
export const CHATGPT_DUPE_APP_URL =
  'https://chatgpt.com/apps/dupe/asdk_app_6949a8ef3c1481918712fd15126dfe6e'

export const IS_TEST =
  process.env.NODE_ENV === 'test' ||
  process.env.TEST === 'true' ||
  process.env.TEST_DATABASE_URL

export const DUPE_STORE_URL = 'https://store.dupe.com'

export const DUPE_COMPARE_TO_COOKIE_KEY = '__dupe_input_compare_to'
export const DUPE_TRADE_TOAST_BANNER_COOKIE_KEY = '__dupe_trade_toast_banner'
export const DUPE_HOME_CATEGORY_SELECTOR_COOKIE_KEY =
  '__dupe_home_category_selector'
export const DUPE_GET_PAID_MODAL_COOKIE_KEY = '__dupe_get_paid_modal'
export const DUPE_APP_PREORDER_MODAL_COOKIE_KEY = '__dupe_app_preorder_modal'
export const DUPE_CHAT_APP_INSTALL_MODAL_COOKIE_KEY =
  '__dupe_chat_app_install_modal'
export const DUPE_CHATGPT_PROMO_MODAL_COOKIE_KEY = '__dupe_chatgpt_promo_modal'
export const DUPE_CHATGPT_PROMO_BANNER_COOKIE_KEY =
  '__dupe_chatgpt_promo_banner'
export const DUPE_RESEARCH_MODAL_COOKIE_KEY = '__dupe_research_modal'

export const IMAGEKIT_URL_ENDPOINT = 'https://assets.dupe.com/'
export const IMAGEKIT_PUBLIC_KEY = process.env.NEXT_PUBLIC_IMAGEKIT_PUBLIC_KEY
export const IMAGEKIT_PRIVATE_KEY = process.env.IMAGEKIT_PRIVATE_KEY

// For new Imagekit account
export const IMAGEKIT_DUPE_URL_ENDPOINT = 'https://ik.imagekit.io/dupe/'
export const IMAGEKIT_DUPE_PUBLIC_KEY = process.env.IMAGEKIT_DUPE_PUBLIC_KEY
export const IMAGEKIT_DUPE_PRIVATE_KEY = process.env.IMAGEKIT_DUPE_PRIVATE_KEY

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]

export const MISC_EXCLUDED_DOMAINS = [
  'serpapi.com',
  'searchapi.io',
  'instagram.com',
  'pin.it',
  'pinterest.com',
  'tiktok.com',
  'balsamhill.com',
]

export const LENS_DOMAINS_TO_EXCLUDE = [
  '3d-baza.com',
  'cgtrader.com',
  'blendermarket.com',
  'renderhub.com',
  '3dexport.com',
  '3dmodels.org',
  'turbosquid.com',
  'moduspace.sg',
  'us.moduspace.sg',
  'blender.org',
  'cults3d.com',
  'grabcad.com',
  'sketchup.com',
  'sketchfab.com',
  'greatcatalog.net',
  'zeelproject.com',
  'artstation.com',
  'bendtrade.com',
  'cinziagrassi.com',
  'farraragency.com',
  'addmodel.com',
  '3docean.net',
  'facequad.com',
  'seededhome.com',
  'cgmood.com',
  'b2brazil.com',
  'dekorate.com',
  'myshopify.com',
  'dhgate.com',
  'wenniejewelry.com',
  '37bracelets.com',
  'lightexcellent.com',
  'cassatex.ro',
  'wemakebeds.co.uk',
  'lovemybedss.co.uk',
  'alibaba.com',
  'tiktok.com',
  // Which? UK consumer-comparison portal — pages are review/link
  // aggregators with affiliate redirect chains, not real PDPs.
  // `.which.one` suffix matching via tldts' `getDomain` covers
  // subdomains like `uk.which.one`.
  'which.one',
  // Our own product-page URLs get picked up by Google Lens when they
  // surface higher than the actual retailer pages. Exclude them so we
  // don't recursively show dupe.com pages as dupes. dupe.link is the
  // preview/redirect domain used by worker routes.
  'dupe.com',
  'dupe.link',
  //
  // NEWS / EDITORIAL / STOCK PHOTO (2026-09-14). A shopper uploaded a press
  // photo of a celebrity and the grid rendered news articles about him as
  // shoppable product cards. Google Lens's `products` tab backfills with
  // generic visually-similar pages when it cannot resolve real listings, which
  // is the normal case for a paparazzi photo — so these arrive through the
  // ordinary product path with no signal that they are articles.
  //
  // ⚠️ THIS LIST IS A STOPGAP, NOT THE FIX. A blocklist cannot win here: the
  // next celebrity photo surfaces four different aggregators. The durable
  // discriminator is already in the upstream payload — an editorial result has
  // neither `extracted_price` NOR `stock_information`, where a real listing has
  // at least one (`getLensResults.ts` maps both). Gating on that is a retrieval
  // policy change affecting chat, /dupes, email digests and discover at once,
  // so it needs its own measurement rather than a ride-along.
  //
  // ⚠️ NOT bare 'yahoo.com' — `isDomainToExclude` matches registrable domain OR
  // full hostname, and shopping.yahoo.com is a real retail surface. Same
  // reasoning as `telemetryDenylist.ts`.
  'news.yahoo.com',
  'smi2.net',
  'smi2.ru',
  'wenxuecity.com',
  'eonline.com',
  'pagesix.com',
  'tmz.com',
  'justjared.com',
  // Stock-photo agencies: the other half of what a press image matches on.
  // These sell licences, never the pictured garment.
  'gettyimages.com',
  'shutterstock.com',
  'alamy.com',
]

export const WSI_DOMAINS = [
  'westelm.com',
  'westelm.ca',
  'westelm.co.uk',
  'westelm.com.mx',
  'potterybarn.com',
  'potterybarn.ca',
  'potterybarnkids.com',
  'potterybarnkids.ca',
  'pbteen.com',
  'pbteen.ca',
  'rejuvenation.com',
  'rejuvenation.ca',
  'williams-sonoma.com',
  'williams-sonoma.ca',
  'markandgraham.com',
  'markandgraham.ca',
  'greenrow.com',
]

export const EXCLUDED_SHOP_KEYS = [
  ...WSI_DOMAINS.flatMap((domain) => [
    `https://${domain}`,
    `https://www.${domain}`,
    `http://${domain}`,
    `http://www.${domain}`,
  ]),
  ...LENS_DOMAINS_TO_EXCLUDE.flatMap((domain) => [
    `https://${domain}`,
    `https://www.${domain}`,
    `http://${domain}`,
    `http://www.${domain}`,
  ]),
  ...MISC_EXCLUDED_DOMAINS.flatMap((domain) => [
    `https://${domain}`,
    `https://www.${domain}`,
    `http://${domain}`,
    `http://www.${domain}`,
  ]),
]

export type ShopifyDiscount = {
  code: string
  value?: number
  percentage?: number
  expires?: Date
  enabled?: boolean
}

export const SHOPIFY_DISCOUNTS: ShopifyDiscount[] = [
  {
    code: 'OMGDUPE',
    percentage: 10,
    enabled: true,
  },
  {
    code: '$DUPE',
    percentage: 10,
    enabled: true,
  },
]

/** Origin of a v2 chat turn's product grid, stamped on funnel events/clicks. */
export type ChatResultPathway = 'paste' | 'image' | 'dupe' | 'text'

export enum AnalyticsEventName {
  VanityPromoSignUpClick = 'Vanity Promo Sign Up Click',
  DupeAIChatFeedback = 'Dupe AI Chat Feedback',
  ResearchFeedback = 'Research Feedback',
  DupeAIChatSubmit = 'Dupe AI Chat Submit',
  DupeAIDrawerOpened = 'Dupe AI Drawer Opened',
  DupeAIFollowUpClicked = 'Dupe AI Follow Up Clicked',
  DupeAIChatToRefine = 'Dupe AI Chat To Refine',
  AIAnalysisOpened = 'AI Analysis Opened',
  AIAnalysisRegenerated = 'AI Analysis Regenerated',
  AIAnalysisFeedback = 'AI Analysis Feedback',
  /** DUP-2387 — fired once per extension side-panel product view when the
   * single-step price-analysis workflow resolves, whatever the outcome.
   * `level` is the bar's own value (`low`/`medium`/`high`/`unknown`) or
   * `none` when the workflow returned null / errored / timed out, so
   * COVERAGE = share of events where level is not `unknown` or `none`.
   * That number gates the price-copy trigger arm in DUP-2386 — do not enable
   * that arm off a flat result without reading this first.
   *
   * ⚠️ FILTER `forced = false` WHEN READING COVERAGE. `?flag=` forces the
   * feature on and is NOT dev-only, so QA page loads fire this event into the
   * same denominator as organic traffic — and QA tends to use a handful of
   * hand-picked hashes, which is exactly the bias that would make coverage look
   * better or worse than it is. `forced` is always present; an absent value
   * means an event predating the property, not an organic one. */
  SidepanelPriceLevelShown = 'Sidepanel Price Level Shown',
  GetPaidModalVisible = 'Get Paid Modal Visible',
  GetPaidModalClick = 'Get Paid Modal Click',
  AppPreorderModalVisible = 'App Preorder Modal Visible',
  AppPreorderModalClick = 'App Preorder Modal Click',
  AppPreorderModalDismissed = 'App Preorder Modal Dismissed',
  AppPreorderButtonClick = 'App Preorder Button Click',
  ChatGPTPromoModalVisible = 'ChatGPT Promo Modal Visible',
  ChatGPTPromoModalClick = 'ChatGPT Promo Modal Click',
  ChatGPTPromoModalDismissed = 'ChatGPT Promo Modal Dismissed',
  ChatGPTPromoBannerVisible = 'ChatGPT Promo Banner Visible',
  ChatGPTPromoBannerClick = 'ChatGPT Promo Banner Click',
  ChatGPTPromoBannerDismissed = 'ChatGPT Promo Banner Dismissed',
  ChatGPTPromoCardClick = 'ChatGPT Promo Card Click',
  DupeMaxPromoCardVisible = 'Dupe Max Promo Card Visible',
  DupeMaxPromoCardClick = 'Dupe Max Promo Card Click',
  /** Mobile FAB variant's X tapped — hides it for 7 days (localStorage).
   *  Carries `slug`, `query`, `source`. */
  DupeMaxPromoCardDismissed = 'Dupe Max Promo Card Dismissed',
  ExtensionPromoBannerClick = 'Extension Promo Banner Click',
  RewardsClaimed = 'Rewards Claimed',
  RewardsClaimClicked = 'Rewards Claim Clicked',
  RewardsCashOutClicked = 'Rewards Cash Out Clicked',
  RewardsHeaderTriggerClicked = 'Rewards Header Trigger Clicked',
  HomepageCategorySelected = 'Homepage Category Selected',
  /** Hero category tab/pill (HomeCategoryPills) clicked to filter the discover
   *  feed. Distinct from HomepageCategorySelected (the IG onboarding modal).
   *  Carries `category`, `label`, `was_active`, `source`. */
  HomepageCategoryPillClicked = 'Homepage Category Pill Clicked',
  /** Hero category toggle (HomeCategoryChips) tapped; switches the headline
   *  words and the input's typed example placeholders. Carries `category`,
   *  `was_active`. */
  HomepageCategoryChipClicked = 'Homepage Category Chip Clicked',
  /** The hero category menu (HomeCategoryChips) opened. Carries `category`
   *  (the one showing when it opened). */
  HomepageCategoryMenuOpened = 'Homepage Category Menu Opened',
  /** Example search pills shown under the homepage input (Fragrances picked).
   *  Carries `category`, `examples` (in shown order), `count`. */
  HomepageExamplesShown = 'Homepage Examples Shown',
  /** An example search pill tapped; it fills the input and does not submit.
   *  Carries `category`, `example`, `position` (1-based). */
  HomepageExampleClicked = 'Homepage Example Clicked',
  /** A card on the /shop page clicked: a curated pick, a deals-row deal, or an
   *  Obsessions collection. Carries `section` (picks | deals | obsessions),
   *  `position`, `kind`, `shop_key`, `category`, `tab`, `pick_id`, `click_id`. */
  ShopPickClicked = 'Shop Pick Clicked',
  /** A category tab on the /shop page selected. Carries `tab`, `was_active`. */
  ShopCategoryTabClicked = 'Shop Category Tab Clicked',
  NoURLCTAShown = 'No URL CTA Shown',
  NoURLCTAClick = 'No URL CTA Click',
  NoURLResultClick = 'No URL Result Click',
  HomepageViewed = 'Homepage Viewed',
  RestyleHeaderCTA = 'Restyle Header CTA',
  RestyleBannerCTA = 'Restyle Banner CTA',
  RestyleBannerVisible = 'Restyle Banner Visible',
  RestyleGenerateLimitReached = 'Restyle Generate Limit Reached',
  RestyleReloadStyle = 'Restyle Reload Style',
  RestyleShareClicked = 'Restyle Share Clicked',
  RestyleInputMenuCTA = 'Restyle Input Menu CTA',
  RestyleToggleImageTapped = 'Restyle Toggle Image Tapped',
  RestyleJobCardClick = 'Restyle Job Card Click',
  RestyleButtonClick = 'Restyle Button Click',
  RestyleRoomSelect = 'Restyle Room Select',
  RestyleRatingSubmitted = 'Restyle Rating Submitted',
  RestyleObjectClick = 'Restyle Object Click',
  RestyleShoppableButtonClick = 'Restyle Shoppable Button Click',
  HomeRecentsClick = 'Home Recents Click',
  /** Fired once per card when a homepage "Keep shopping" rail
   *  (HomeRecentActivity) card scrolls into the rail's viewport — the
   *  denominator for `Home Recents Click`, carrying the same `card_type` /
   *  `position` / `slot_total`, so per-slot and per-type CTR is a single-system
   *  PostHog formula. Rooted on the carousel so off-rail cards don't over-count. */
  HomeRecentsImpression = 'Home Recents Impression',
  // Desktop homepage quick-search widget (HomeSearchWidget). Submits are
  // tracked downstream via SearchTermEntered/PerformedDupe with
  // `source: 'home-search-widget-compact'`; these cover the funnel above submit.
  HomeSearchWidgetViewed = 'Home Search Widget Viewed', // faded into view (once per mount)
  HomeSearchWidgetFocused = 'Home Search Widget Focused', // user tapped/focused the input (once per mount)
  HomeSearchWidgetDismissed = 'Home Search Widget Dismissed', // user closed via the "X" (once per mount)
  ProjectDrawerTriggerVisible = 'Project Drawer Trigger Visible',
  ProjectDrawerResultClicked = 'Project Drawer Result Clicked',
  ProjectDrawerEditMode = 'Project Drawer Edit Mode',
  ProjectDrawerTriggered = 'Project Drawer Triggered',
  ProjectProductAdded = 'Project Product Added',
  ProjectProductRemoved = 'Project Product Removed',
  ApplePayButtonClicked = 'Apple Pay Button Clicked',
  SurvicateSurveyDisplayed = 'Survicate Survey Displayed',
  SurvicateQuestionAnswered = 'Survicate Question Answered',
  SurvicateSurveyCompleted = 'Survicate Survey Completed',
  SurvicateSurveyClosed = 'Survicate Survey Closed',
  DeepSearchAuthModalOpened = 'Deep Search Auth Modal Opened',
  DeepSearchAuthCallback = 'Deep Search Auth Callback',
  DeepSearchEnabled = 'Deep Search Enabled',
  DeepSearchSeeResultsClicked = 'Deep Search See Results Clicked',
  DeepSearchFeedbackClicked = 'Deep Search Feedback Clicked',
  DupeAssistantPaywallSkip = 'Dupe Assistant Paywall Skip',
  DupeAssistantInView = 'Dupe Assistant In View',
  DupeAssistantTriggered = 'Dupe Assistant Triggered',
  DupeAssistantChooseIntent = 'Dupe Assistant Choose Intent',
  DupeAssistantSubmit = 'Dupe Assistant Submit',
  DupeAssistantSubmitError = 'Dupe Assistant Submit Error',
  DupeAssistantPaywallClick = 'Dupe Assistant Paywall Click',
  ClickedInputMediaMenu = 'Clicked Input Media Menu',
  ClickedInputMedia = 'Clicked Input Media',
  DupeResultsSort = 'Dupe Results Sort',
  CanvasImageClick = 'Canvas Image Click',
  AuthModalOpened = 'Auth Modal Opened',
  /** Impression for the "signup-incentive-wall" experiment: the locked sign-up
   *  wall rendered over the results page (logged-out desktop shoppers). */
  SignupWallShown = 'Sign Up Wall Shown',
  /** Results-page post-signup onboarding (desktop): flow mounted. */
  ResultsOnboardingStarted = 'Results Onboarding Started',
  /** Results-page onboarding: a step was answered or skipped (analytics-only). */
  ResultsOnboardingStep = 'Results Onboarding Step',
  /** Results-page onboarding: finished — results revealed. */
  ResultsOnboardingCompleted = 'Results Onboarding Completed',
  /** Results-page onboarding: the profile write of the shopping + style answers
   *  settled, with `saveResult` 'saved' | 'failed'. The write is fire-and-forget
   *  — the reveal never waits on it — so this fires whenever the write resolves,
   *  which may be before or after the reveal. This is the DUP-1875 drop signal:
   *  `saveResult: 'failed'` counts a shopper whose picks were lost and who will
   *  be re-asked next sign-in. Not emitted when no write was attempted (read
   *  `saveAttempted` on the completion event for that). */
  ResultsOnboardingAnswersSaved = 'Results Onboarding Answers Saved',
  /** Visual taste onboarding (deck variant): flow mounted. */
  VisualOnboardingStarted = 'Visual Onboarding Started',
  /** Visual taste onboarding: a card was rated (−1/0/+1) — analytics-only. */
  VisualOnboardingCardRated = 'Visual Onboarding Card Rated',
  /** Visual taste onboarding: finished the deck — reveal shown. */
  VisualOnboardingCompleted = 'Visual Onboarding Completed',
  /** Brand-led onboarding (brand picker + style grid): flow mounted. Carries a
   *  `flow` prop so its funnel can be sliced against the other variants. */
  BrandOnboardingStarted = 'Brand Onboarding Started',
  /** Brand-led onboarding: a step was answered or advanced past (analytics-only;
   *  `skipped` is true when nothing was picked). */
  BrandOnboardingStep = 'Brand Onboarding Step',
  /** Brand-led onboarding: finished — carries the deduced department + picks. */
  BrandOnboardingCompleted = 'Brand Onboarding Completed',
  /** Shared, arm-agnostic exit event fired by BOTH the results-wall onboarding
   *  arms (control question flow + test brand flow) whenever the shopper leaves
   *  onboarding to results. `variant` = 'question' | 'brand'; `completedVia` =
   *  'finish' | 'skip'. Exposure (`$feature_flag_called`) is the denominator, so
   *  this single numerator gives a clean per-variant completion / skip / engaged-
   *  completion rate without unioning the disjoint per-arm event names. */
  OnboardingCompleted = 'Onboarding Completed',
  /** Shared, arm-agnostic top-of-funnel event fired by BOTH results-wall
   *  onboarding arms (control question flow + test brand flow) when the flow is
   *  first shown. `variant` = 'question' | 'brand'. Each arm still fires its own
   *  named started event too; this gives one common exposure/started metric so a
   *  dashboard keyed on it doesn't read the other arm as zero. */
  OnboardingStarted = 'Onboarding Started',
  AuthModalClosed = 'Auth Modal Closed',
  AuthModalSendCode = 'Auth Modal Send Code',
  AuthModalSendCodeError = 'Auth Modal Send Code Error',
  AuthModalVerifyCodeError = 'Auth Modal Verify Code Error',
  AuthModalOnboardingSubmit = 'Auth Modal Onboarding Submit',
  AuthModalOnboardingPaywallValidate = 'Auth Modal Onboarding Paywall Validate',
  DupeTradeScheduleCallClicked = 'Dupe Trade Schedule Call Clicked',
  DupeTradeBannerClicked = 'Dupe Trade Banner Clicked',
  DupeTradeBannerClosed = 'Dupe Trade Banner Closed',
  FeaturedDupeDirectCardClicked = 'Featured Dupe Direct Card Clicked',
  DupeDirectPicksMatch = 'Dupe Direct Picks Match',
  DupeDirectPicksView = 'Dupe Direct Picks View',
  DupeDirectModalOpen = 'Dupe Direct Modal Open',
  DupeDirectViewAllClicked = 'Dupe Direct View All Clicked',
  DupeDirectButtonClicked = 'Dupe Direct Button Clicked',
  DupeDirectCheckoutClicked = 'Dupe Direct Checkout Clicked',
  DupeDirectImageZoomed = 'Dupe Direct Image Zoomed',
  DupeDirectRailViewed = 'Dupe Direct Rail Viewed',
  SmartBannerView = 'Smart Banner View',
  SmartBannerClickout = 'Smart Banner Clickout',
  SmartBannerExit = 'Smart Banner Exit',
  DupeDiscoverPreviewClick = 'Dupe Discover Preview Click',
  ResearchReportCardClick = 'Research Report Card Click',
  PicksCardClick = 'Picks Card Click',
  PicksCardImpression = 'Picks Card Impression',
  PicksSeeAllClick = 'Picks See All Click',
  // Fires when a tile badged "Partner" (pinnedSponsor) gets a plain click but
  // resolves to no advertiser redirect — i.e. the sponsored click-out silently
  // fell through to normal collection navigation. The dominant cause is a stale
  // gift-guide cache entry serialized before `sponsorUrl` existed, so this is
  // the production tripwire for that regression.
  PicksSponsorClickMisconfigured = 'Picks Sponsor Click Misconfigured',
  GiftGuideViewed = 'Gift Guide Viewed',
  GiftGuideProductClicked = 'Gift Guide Product Clicked',
  GiftGuidePageEngaged = 'Gift Guide Page Engaged',
  HubPageViewed = 'Hub Page Viewed',
  HubPageEngaged = 'Hub Page Engaged',
  HubProductClicked = 'Hub Product Clicked',
  HubReportClicked = 'Hub Report Clicked',
  HubSiblingClicked = 'Hub Sibling Clicked',
  SeoReportViewed = 'SEO Report Viewed',
  SeoReportEngaged = 'SEO Report Engaged',
  SeoReportProductClicked = 'SEO Report Product Clicked',
  SeoReportProductDrawerOpened = 'SEO Report Product Drawer Opened',
  SeoReportSourcesOpened = 'SEO Report Sources Opened',
  SeoReportRelatedClicked = 'SEO Report Related Clicked',
  AppBannerModalShown = 'App Banner Modal Shown',
  AppBannerModalOpenAppClicked = 'App Banner Modal Open App Clicked',
  AppBannerModalStaySafariClicked = 'App Banner Modal Stay Safari Clicked',
  AppLinkClicked = 'App Link Clicked',
  TodayShowVideoPlayed = 'Today Show Video Played',
  CheckPageViewed = 'Check Page Viewed',
  CheckPageAuthShown = 'Check Page Auth Shown',
  /** User toggled the "Also search <partner>" checkbox. Fires ONLY on a real
   *  interaction — the box defaults to checked, so the absence of this event
   *  means the default was accepted, not that the user opted in. Pair with the
   *  `compare` property on `Performed Dupe` / `Check Page Button Clicked` for
   *  the resulting state at submit time. */
  AlsoSearchToggled = 'Also Search Toggled',
  CheckPageButtonClicked = 'Check Page Button Clicked',
  CheckPageReasonSelected = 'Check Page Reason Selected',
  CheckPageTimerSubmit = 'Check Page Timer Submit',
  CheckPageAuthClicked = 'Check Page Auth Clicked',
  CheckPageAuthSuccess = 'Check Page Auth Success',
  CheckPageAuthLaterClicked = 'Check Page Auth Later Clicked',
  UserSignedIn = 'User Signed In',
  UserSignedInError = 'User Signed In Error',
  /** Fires once when a brand-new account is created (server-authoritative:
   *  maybe-register `existing === false`), client-side. The activation trigger
   *  for the post-registration "How did you hear about us?" survey (DUP-1563) and
   *  a clean new-user acquisition signal. Distinct from `UserSignedIn`, which also
   *  fires for returning logins. */
  RegistrationCompleted = 'Registration Completed',
  GuestAccountCreated = 'Guest Account Created',
  SearchNoResults = 'Search No Results',
  /**
   * What the chat's SEED turn actually did — fired once per seed turn, from
   * `BrainChat.tsx`.
   *
   * The seed turn is the local, zero-LLM first turn: a source-product card
   * plus a grid of dupes fetched client-side. It renders on the SEO product
   * pages, on a fresh `/chats/{hash}` entry, and in the extension side panel.
   *
   * ⚠️ WHY THIS IS NOT `PerformedDupe`, and must not become it.
   *
   * `PerformedDupe` means "a dupe search ran and produced results" — a rule
   * enforced by `performedDupeEmitterInventory.spec.ts` and relied on by a
   * documented zero-result recovery journey. The seed turn breaks that rule in
   * both directions: nobody performed a search (the page did, on mount), and
   * the outcome this event exists to capture is precisely the one where NO
   * search ran. Widening `PerformedDupe` to cover it would change the volume
   * and the meaning of ~70% of that event.
   *
   * ⚠️ `SearchNoResults` is not it either: that name asserts the search ran.
   *
   * Carries `search_outcome`, which separates the four states that matter —
   * `results` / `zero` / `failed` / `never_ran` — plus `surface`, `in_iframe`
   * and `country_known`. ⛔ `never_ran` must never be folded into `zero`: a
   * chat telling a shopper it found nothing when the search was never issued
   * is the defect this event was added to make countable.
   *
   * First-party sinks only. A seed turn is not a conversion and not a visit.
   */
  ChatSeedOutcome = 'Chat Seed Outcome',
  /** DUP-1903 — the marketing (Dupe Drop) opt-in decision on the sign-up screen
   *  (`account_creation` arm). Carries `granted`, `surface`, `jurisdiction`,
   *  `method` ('implied' | 'active'), `optinPlacement`, and `authMethod`, so the
   *  arm's opt-in can be read without a persistence change (the subscription
   *  itself is the durable record). */
  MarketingConsentDecided = 'Marketing Consent Decided',
  /** DUP-1903 — a marketing (Dupe Drop) subscribe actually SUCCEEDED. Fired from
   *  every surface that subscribes: both onboarding flows and the sign-up-screen
   *  flush. One event so the opt-in placement experiment has a single primary
   *  metric — without it each arm would have to be read off a different
   *  completion event (`Results Onboarding Completed` / `Brand Onboarding
   *  Completed` / `Marketing Consent Decided`), which isn't comparable. Carries
   *  `optinPlacement`, `optinSurface`, `flow`, and the consent regime. */
  NewsletterSubscribed = 'Newsletter Subscribed',
  /**
   * DUP-2460 — the email ask was SHOWN to a phone sign-up. The denominator.
   *
   * Deliberately its own event rather than reusing `Marketing Consent Decided`
   * filtered to `authMethod: 'phone', hasEmail: false`. That filter does
   * currently isolate this screen, but only because `useFlushMarketingConsent`
   * defers for exactly that combination — so the denominator would silently
   * break the day anyone changed the defer, and it requires knowing the trick
   * to build the funnel at all. An explicit pair is worth one extra event.
   */
  PhoneEmailRequested = 'Phone Email Requested',
  /**
   * DUP-2460 — a phone sign-up handed over an email address on the code screen.
   *
   * The completion half of that ask, and it exists because `Newsletter
   * Subscribed` cannot answer the ticket's actual question. That event fires
   * only when someone OPTS IN, but a decliner's address is still an address:
   * it goes on the account for transactional mail and it is exactly as much a
   * win for reachability. Measuring the screen off the subscribe event alone
   * would score the decline path at zero and understate the feature by however
   * many people say "no thanks" and then type their email anyway.
   *
   * Pairs with `PhoneEmailRequested`, which fires when the ask APPEARS:
   *
   *   asked    = Phone Email Requested
   *   captured = Phone Email Captured
   *   rate     = captured / asked
   *
   * and `subscribed` splits the captures into the two consent outcomes.
   *
   * Use that pair, NOT `Marketing Consent Decided` filtered to
   * `authMethod: 'phone', hasEmail: false`. That filter does isolate this screen
   * today, but only because `useFlushMarketingConsent` defers for exactly that
   * combination — so it would stop being a valid denominator the moment anyone
   * touched the defer, silently. See `PhoneEmailRequested` above.
   */
  PhoneEmailCaptured = 'Phone Email Captured',
  /**
   * A phone sign-up typed an email that already belongs to a DIFFERENT Dupe
   * account, and the write was refused (HTTP 409 `email_in_use`) instead of
   * minting a second account with the same address. Counts how often the
   * "already have a Google/Apple account, signed in with phone" case happens.
   *
   * `provider` is how the existing account signs in (`google` | `apple` |
   * `phone` | `unknown`), `optedIn` whether they ticked the Dupe Drop. Never
   * carries the email itself.
   */
  PhoneEmailInUse = 'Phone Email In Use',
  /**
   * The phone sign-up's email belongs to a Google/Apple account, so instead of
   * a dead end the email step offered to join the two ("Continue with
   * Google/Apple"). `provider`: `google` | `apple`. Never carries the email.
   *
   *   offered   = Phone Account Join Offered
   *   completed = Phone Account Join Completed
   *   rate      = completed / offered
   */
  PhoneAccountJoinOffered = 'Phone Account Join Offered',
  /**
   * The shopper proved the existing account and is now signed in to it.
   * `phoneAttached` is false when the phone number could not be moved onto it
   * (the account already had one), true when it was. `provider` as above.
   */
  PhoneAccountJoinCompleted = 'Phone Account Join Completed',
  /**
   * The join did not go through. `stage` says where: `cancelled` (closed the
   * popup; they stay on the offer), `popup`, `credential`, `wrong_account`
   * (picked a different Google/Apple account), `sign_in`, `save`, `server`
   * (the API refused — they are still signed in to their existing account,
   * just without the phone number), `db` (Firebase was joined but our rows
   * were not — fix by hand from the api log), or `unexpected` (something
   * threw).
   * `provider` as above.
   */
  PhoneAccountJoinFailed = 'Phone Account Join Failed',
  UserManagePlanClicked = 'Manage Plan Clicked',
  ExtensionInstalled = 'Extension Installed',
  ExtentionPostInstall = 'Extension Post Install',
  ExtensionPostUninstall = 'Extension Post Uninstall',
  ExtensionInstalledWithUser = 'Extension Installed With User',
  ViewedDupeExtensionResultPage = 'Viewed Dupe Extension Result Page',
  ExtensionUpdated = 'Extension Updated',
  /** DUP-1831 — "Continue shopping" on the cart verdict card (affiliate click-out). */
  ExtensionCartVerdictCtaClicked = 'Extension Cart Verdict CTA Clicked',
  /**
   * DUP-1831 — a "Worth comparing" row on the cart verdict card (affiliate
   * click-out). Carries `position`, which is how we learn whether the third row
   * ever earns the card height it costs.
   */
  ExtensionCartVerdictAlternativeClicked = 'Extension Cart Verdict Alternative Clicked',
  /**
   * DUP-1950 — the cart verdict card reached a SETTLED state and showed a
   * message. Fired once per card, never on mount or for a loading/failed card,
   * so clicks and dismissals finally have a denominator. Carries `card_state`
   * (which of the 8 `CARD_COPY` wordings was on screen), `deal_quality`,
   * `alternatives_rendered`, `price_claim_reason` (no-opinion cards) and an
   * optional `copy_variant_id` for a future wording test.
   */
  ExtensionCartVerdictShown = 'Extension Cart Verdict Shown',
  /**
   * DUP-1950 — the cart verdict card went away. `reason` distinguishes the three
   * paths — `x_button`, `auto` (the countdown expired unread) and `click_through`
   * (the CTA) — because "ignored until it vanished" and "actively closed" mean
   * opposite things. Only fired for a card that was shown first.
   */
  ExtensionCartVerdictDismissed = 'Extension Cart Verdict Dismissed',
  /**
   * DUP-2087 — an install cleared every DURABLE rung of the review-prompt ladder
   * (not answered, not in cooldown, old enough, under the lifetime cap, armed,
   * and the trigger satisfied).
   *
   * ⚠️ This is NOT an impression. The venue and feature-flag rungs have not been
   * applied, and in Phase 1 there is no prompt UI at all — it exists so we can
   * measure our real eligible-population rate from first-party data before
   * turning anything on. Do not build a "prompt shown" or conversion-rate
   * readout on it; `ExtensionReviewPromptShown` is the impression event and
   * lands with the UI.
   *
   * Carries `trigger` and `shape` so a readout can compare configurations, plus
   * `purchases_since_arm` / `opens_since_arm` to show how much activity the bar
   * actually took.
   */
  ExtensionReviewPromptEligible = 'Extension Review Prompt Eligible',
  /**
   * DUP-2087 — the prompt actually rendered. THIS is the impression event and
   * the denominator for any "what share of prompted people rate us" question.
   * Carries `trigger` and `shape`.
   */
  /**
   * DUP-2087 — the COUNTERFACTUAL: everything cleared except the flag.
   *
   * Fires whether the flag is on or off, carrying `flag_enabled`, so one metric
   * answers "how many asks would we make" independently of the rollout
   * percentage. That is the number needed to decide whether a trigger is worth
   * shipping, and it cannot be read off `Shown` while the flag is at 0%.
   *
   * ⚠️ Rate-limited by a SHADOW cooldown and cap that mirror the real ones. In
   * dark mode nothing writes the real cooldown, so without that this would fire
   * on every ordinary page load of every eligible install — counting page views,
   * not asks, and overstating by orders of magnitude.
   */
  ExtensionReviewPromptWouldAsk = 'Extension Review Prompt Would Ask',
  ExtensionReviewPromptShown = 'Extension Review Prompt Shown',
  /**
   * DUP-2087 — they clicked through to the Web Store listing.
   *
   * ⚠️ This is `review_intent`, NOT a posted review. Chrome gives no callback,
   * so we cannot know whether a review was left. Never rename this to something
   * that implies the outcome, and never report it as "reviews collected".
   */
  ExtensionReviewPromptStoreClicked = 'Extension Review Prompt Store Clicked',
  /**
   * DUP-2087 — they used the second door and told us what was missing.
   *
   * ⚠️ Deliberately carries NO page context. Pie attaches the referring URL to
   * the event bearing free-text feedback, so whatever someone was browsing
   * travels with their complaint. We do not do that.
   */
  ExtensionReviewPromptFeedbackSubmitted = 'Extension Review Prompt Feedback Submitted',
  /**
   * DUP-2087 — dismissed without picking a door. A dismissal is NOT an answer:
   * it starts the cooldown (already written on show) but never the permanent
   * stop, so the person can be asked again later.
   */
  ExtensionReviewPromptDismissed = 'Extension Review Prompt Dismissed',
  /** Deal Check fake-door test — the prompt rendered on a qualifying page. */
  DealCheckPromptShown = 'Deal Check Prompt Shown',
  /** Deal Check fake-door test — clicked "Turn on Deal Check". */
  DealCheckPromptClicked = 'Deal Check Prompt Clicked',
  /** Deal Check fake-door test — dismissed without activating; starts the 24h cooldown. */
  DealCheckPromptDismissed = 'Deal Check Prompt Dismissed',
  /** Deal Check fake-door test — activated permanently for this install. */
  DealCheckActivated = 'Deal Check Activated',
  /**
   * A link OUT of the store-profile panel, to a shop.
   *
   * ⚠️ The panel shipped able to say how it SETTLED (`deal_check_panel_settled`)
   * and nothing about what anyone did in it, so "which block earns clicks" was
   * unanswerable for a feature whose whole purpose is earning them. Carries
   * `block` so the four surfaces — popular, price-drop, similar, also-browse —
   * can be compared, which is the question that decides what the panel should
   * lead with.
   *
   * ⚠️ NOT a substitute for the `src=ext-deal-check-*` tag on the redirect.
   * That is the revenue-side record and the only one that survives the click;
   * this one measures intent, including clicks that never land.
   */
  StoreProfileLinkClicked = 'Store Profile Link Clicked',
  /** The store profile failed to load and the shopper asked for it again. */
  StoreProfileRetried = 'Store Profile Retried',
  /** Tapped the next-section peek to jump down the profile. */
  StoreProfileJumped = 'Store Profile Jumped',
  /** The insider line was shown under the store name (kind and tier, no text). */
  StoreProfileInsiderShown = 'Store Profile Insider Shown',
  /** The insider line was opened to show the items it names (kind, tier, items; no text). */
  StoreProfileInsiderExpanded = 'Store Profile Insider Expanded',
  /** Deal Check fake-door test — a later shop page view in the same session, after activation. */
  DealCheckActivePageView = 'Deal Check Active Page View',
  PromoClickWayfair = 'Promo Click Wayfair',
  CountrySelector = 'Country Selector',
  PerformedDupe = 'Performed Dupe',
  /** Ads-conversion-only twin of `PerformedDupe` (DUP-2091), fired per SEARCH
   *  from the same call sites as `dupe_performed_manual2` (Google Ads/TikTok)
   *  — deliberately NOT the same event as `PerformedDupe`, whose chat gate is
   *  once-per-THREAD (see the doc on `trackGoogleAdsDupePerformed`). Routes
   *  to PostHog only (`skipGA: true` — Google/TikTok already get their own
   *  `dupe_performed_manual2`).
   *
   *  This is Meta's SERVER-side transport, feeding the PostHog "Meta Ads
   *  Conversions" CAPI destination. It runs ALONGSIDE the browser pixel's
   *  `Performed Dupe` (`apps/web/src/lib/analytics/metaConversions.ts`), not
   *  instead of it; both stamp the same per-search id so Meta collapses the
   *  pair on `event_id`.
   *
   *  ⛔ This name is a PREFIX-EXTENSION of `PerformedDupe`, so any analytics
   *  filter using `contains` / `LIKE 'Performed Dupe%'` / `startsWith` sweeps
   *  this event into that one's counts. The cadences differ (this fires per
   *  SEARCH, chat's `PerformedDupe` once per THREAD), so the inflation is
   *  uneven rather than a constant factor anyone would spot. Match by exact
   *  equality. See docs/references/also-search-instrumentation.md — the same
   *  mistake overstated the old chat page 25x via a "contains /chats/" filter.
   *
   *  ⚠️ As of 2026-09-03 nothing consumes this event: the destination is
   *  disabled, still triggers on `Performed Dupe`, and its `eventId` input
   *  still holds the `{event.uuid}` default rather than reading our
   *  `event_id` property. See `metaAdsConversion.ts` and the Motion 2
   *  pre-launch gates in docs/features/active/zaraz-migration/TODO.md. */
  PerformedDupeAdsConversion = 'Performed Dupe Ads Conversion',
  PerformedDupeInterstitial = 'Performed Dupe Interstitial',
  ViewedDupeResultPage = 'Viewed Dupe Result Page',
  LoadMoreResults = 'Load More Results',
  ExtensionPageViewed = 'Extension Page Viewed',
  ExtensionPriceElementDisplayed = 'Extension Price Element Displayed',
  ExtensionPriceElementClicked = 'Extension Price Element Clicked',
  ExtensionActivated = 'Extension Activated',
  ExtensionSessionStart = 'Extension Session Start',
  ExtensionHeartbeat = 'Extension Heartbeat',
  ResultsExtensionWallShown = 'Results Extension Wall Shown',
  ResultsExtensionWallClick = 'Results Extension Wall Clicked',
  ResearchModalVisible = 'Research Modal Visible',
  ResearchModalDismissed = 'Research Modal Dismissed',
  ResearchModalActivated = 'Research Modal Activated',
  ResearchHomepageToggleClicked = 'Research Homepage Toggle Clicked',
  ResearchQuerySubmitted = 'Research Query Submitted',
  ResearchStarted = 'Research Started',
  ResearchError = 'Research Error',
  ResearchFailure = 'Research Failure',
  ResearchConfirmFailed = 'Research Confirm Failed',
  ResearchPhaseCompleted = 'Research Phase Completed',
  ResearchReportViewed = 'Research Report Viewed',
  ResearchArchetypeSelected = 'Research Archetype Selected',
  ResearchRefinementSelected = 'Research Refinement Selected',
  ResearchProductDetailsTriggered = 'Research Product Details Triggered',
  ResearchBuyButtonClicked = 'Research Buy Button Clicked',
  ResearchShareReportClicked = 'Research Share Report Clicked',
  ResearchFollowUpChatClicked = 'Research Follow Up Chat Clicked',
  ResearchFollowUpChatSubmitted = 'Research Follow Up Chat Submitted',
  SearchTermEntered = 'Search Term Entered',
  /** Submit-time INTENT event for a homepage URL paste — the URL-shaped
   *  sibling of `SearchTermEntered` (which covers the text-shaped submit).
   *
   *  This exists because `PerformedDupe` could not honestly be fired here.
   *  Until DUP-2188 the homepage URL branch emitted `PerformedDupe` at
   *  submit AND the destination chat emitted it again when products landed,
   *  so one paste produced two of them — in PostHog, in Plausible, in
   *  Tinybird, in Podscribe, and (until Gate 4) in Meta.
   *
   *  The duplication could only be resolved in this direction. `PerformedDupe`
   *  carries `results_count`, `zero_results`, `organic_results_count` and
   *  `picks_results_count`, none of which exist at submit time, and a
   *  documented zero-result recovery journey keys on `zero_results === true`
   *  (docs/references/lifecycle-event-contract.md). The settled emitter is the
   *  only place the event's own definition can be satisfied, so the settled
   *  emitter keeps the name and the submit-time one changed.
   *
   *  ⚠️ It carries the FULL homepage payload, not a trimmed one, and that is
   *  deliberate: this is the only surface stamping the affiliate-router
   *  props (`routerArm`/`routerCategory`/`routerMerchant`/`routerConfidence`/
   *  `routerFallbackReason`) and the only checkbox-bearing surface in the
   *  `paste`/`submit`/`enter`/`queryParam` lane, so it is the sole source of
   *  the homepage "Also search" opt-out rate. Dropping a property here makes
   *  that rate read 0% rather than "no data".
   *  See docs/references/also-search-instrumentation.md.
   *
   *  ⛔ Intent, not conversion — first-party sinks only (PostHog + Tinybird).
   *  Do NOT add `sendToFb`, `sendToPlausible` or `sendToPodscribe`: each of
   *  those is also fired by the settled emitter, so sending from both is the
   *  same double-count in a different pipe.
   *
   *  ⚠️ Deliberate asymmetry with its text sibling: `SearchTermEntered` passes
   *  no options, so it still reaches Zaraz/GA4, while this one sets
   *  `skipGA: true` and does not. The reason is that this event is NEW —
   *  leaving Zaraz on would mint a GA4 event nobody asked for and bill Zaraz
   *  for every homepage paste, whereas the text sibling's GA4 presence is
   *  pre-existing behaviour nobody asked to change. Consequence to know before
   *  reading a GA4 funnel: GA4 sees text-search intent but NOT URL-paste
   *  intent. One-line flip if that ever becomes the wrong trade. */
  SearchUrlSubmitted = 'Search Url Submitted',
  SearchResultClicked = 'Search Result Clicked',
  ChatsV2MessageSent = 'Chats v2 Message Sent',
  /** Server-side, fires once per brain tool invocation in a v2 chat turn (DUP-728).
   *  Properties carry `toolName` (+ threadId, model) so PostHog can break down
   *  which tools are used and correlate with product analytics. Personless. */
  ChatsV2ToolCalled = 'Chats v2 Tool Called',
  /** Server-side, fires once per brain tool RESULT in a v2 chat turn (DUP-728/996).
   *  Carries `toolStatus` (ok | error | no_data) + `errorCode` + `durationMs` so PostHog
   *  can chart per-tool error/empty rate (e.g. a getProductReviews no_data spike after
   *  a rank = a handoff regression) — the call-time `ChatsV2ToolCalled` can't see outcome.
   *  (Property is `toolStatus`, not bare `status`, to avoid colliding with PostHog's
   *  project-wide numeric `status` property definition.)
   *  NOTE: `no_data` currently reflects only tools that set `noDataFound`
   *  (getProductReviews / getProductPricing). Tools that signal emptiness
   *  differently — e.g. an empty showProducts / rankResultSet list — report
   *  `ok`; extend `deriveToolResultStatus` if an empty-grid signal is needed.
   *  Personless. */
  ChatsV2ToolResult = 'Chats v2 Tool Result',
  /** Fires the moment a user taps the thumbs-up / thumbs-down toolbar under an assistant reply (before any follow-up modal). */
  ChatsV2FeedbackClicked = 'Chats v2 Feedback Clicked',
  /** brain-profiles Phase 5: tap on an evidence card (social insights quote
   *  link-out / video review card) rendered under a chat message. */
  ChatEvidenceClicked = 'Chat Evidence Clicked',
  /** Fires when the user submits the optional follow-up modal with issueType / details. */
  ChatsV2FeedbackSubmitted = 'Chats v2 Feedback Submitted',
  /** Fires when the user cancels / dismisses the follow-up modal without submitting details. */
  ChatsV2FeedbackDismissed = 'Chats v2 Feedback Dismissed',
  /** Submit-to-first-product latency on the v2 chat — the unified-route mirror of
   *  v1 `DupeAIChatFirstResult`. Fires once per stream, the first time a turn's
   *  `tool_result` yields a non-empty product set. Carries `msSinceSubmit`,
   *  `resultCount`, `threadId`, and (when the turn anchored on a source product)
   *  `hash` + `shopKey`. `pathway` identifies paste/image/dupe/text turns. Lets the
   *  v1-vs-v2 product-display comparison join on the same retailer/result keys
   *  instead of only thread id. */
  ChatsV2FirstResult = 'Chats v2 First Result',
  /** Brain v2 user got a clarifying question and bailed without answering it. Fires
   *  when a thread has an unanswered last-assistant question and one of the bail
   *  signals trips: tab close (`pagehide`), prolonged hidden (`visibilitychange` >
   *  60s), or unmount of the BrainChat without an intervening submit. Carries
   *  `turnIndex` (Q1 / Q2 / …), `questionPreview` (first 80 chars), `threadId`,
   *  `elapsedSinceQuestionMs`, `bailReason` (`pagehide` | `hidden_timeout` |
   *  `unmount`). Used to size the "asked but no products" failure mode and decide
   *  whether the "show matches anyway" follow-up is worth building. */
  ChatsV2QuestionBail = 'Chats v2 Question Bail',
  /** Brain v2 user saw a clarifying question card on screen. Fires once per
   *  unique question (deduped on the assistant message's `questionId` =
   *  `lastAssistantQuestion.id`, kept in `seenQuestionShownIdsRef` so
   *  dismiss-then-restore via the products-on-ask "Refine" chip doesn't
   *  re-emit) the moment the card first renders. The denominator for
   *  `ChatsV2QuestionBail` and any per-question-shown rate (e.g. answer
   *  rate, clicks-during-question). Carries `turnIndex` (Q1 / Q2 / …),
   *  `questionPreview` (first 80 chars), `threadId`,
   *  `chat_experience: 'v2'` (auto-injected via useAnalytics). Originally
   *  added to size the cannibalization signal in experiment 369616
   *  (chats-v2-show-products-on-ask) — without it we could only measure
   *  bails per exposed user, not per question shown. Note: currently
   *  fires only on the `lastAssistantQuestion` path (legacy single-question
   *  flow); the `chats-v2-batched-clarify` carousel path is not yet
   *  instrumented (see PR #3355 follow-up). */
  ChatsV2QuestionShown = 'Chats v2 Question Shown',
  /** Fires when a user expands or contracts the inline product-row grid via
   *  the "View all N results" / "Show less" toggle on a v2 chat assistant
   *  turn. Carries `expanded` (true on expand, false on contract),
   *  `productCount` (total products in the row), `threadId`, and
   *  `chat_experience: 'v2'` (auto-injected). Lets us measure how often
   *  users dig past the truncated 1–2 screen view. */
  ChatsV2ProductRowToggle = 'Chats v2 Product Row Toggle',
  /** Fired when the user taps a follow-up chip above the chats-v2 input.
   *  Carries `chip` (the label text), `chipKind` (query | deepResearch |
   *  brandScope | compare, for the smart-follow-ups feature), and
   *  `threadId`. */
  ChatsV2FollowUpChipTapped = 'Chats v2 Follow-Up Chip Tapped',
  /** Fired once per rendered smart-follow-up chip set (the denominator
   *  for chip CTR). Carries `chipCount`, `kinds` (array of chip kinds),
   *  `source` ('smart' | 'legacy'), and `threadId`. See
   *  docs/features/done/chat-follow-up-chips/. */
  ChatsV2FollowUpChipsShown = 'Chats v2 Follow-Up Chips Shown',
  /** EXP-A2 XL (mechanic M10): the post-grid context-pill row rendered under a
   *  paste/image turn's grid. Fires once per unique visible pill set (deduped
   *  on threadId + question/option signature). The denominator for
   *  `Chats v2 Context Pills Answered`. Carries `questionCount`,
   *  `axes` (the question ids), `threadId`, `chat_experience: 'v2'`
   *  (auto-injected). */
  ChatsV2ContextPillsShown = 'Chats v2 Context Pills Shown',
  /** EXP-A2 XL (mechanic M10): the shopper submitted the context-pill row.
   *  Fires once on submit (including the implicit skip). Carries
   *  `selectedCount` (total pills selected across questions), `skipped` (true
   *  when nothing was selected), `questionCount`, `threadId`,
   *  `chat_experience: 'v2'` (auto-injected). */
  ChatsV2ContextPillsAnswered = 'Chats v2 Context Pills Answered',
  /** User answered a clarifying question card by selecting an option or
   *  submitting custom text. Fires on every answered question. Carries
   *  `questionId`, `questionPreview` (first 80 chars), `selectedLabel`,
   *  `isCustomText` (true when free-typed), `isDupeMaxx` (true for the
   *  injected Dupe Maxx shortcut option), `dupeMaxxSlug` (e.g. "cloud-couch"
   *  when isDupeMaxx is true), `threadId`. */
  ChatsV2QuestionAnswered = 'Chats v2 Question Answered',
  /** Submit-to-first-result-render latency on v1 chat. Fires once per page
   *  mount, the first time the dupes payload settles to a non-empty list.
   *  Carries `msSinceSubmit` from the homepage submit handoff and
   *  `resultCount` (the number of `matches` in that first non-empty
   *  payload). The count lets us run apples-to-apples product-display
   *  comparisons against v2's `brain_turn_outcome.brain_product_count`. */
  DupeAIChatFirstResult = 'Dupe AI Chat First Result',
  /** Server-side capture of brain API error (terminal — propagated to the client as an `error` SSE frame or thrown from the controller). Carries `route`, `errorClass`, `errorMessage`, `threadId`, `chat_experience: 'v2'`. */
  BrainAPIError = 'Brain API Error',
  /** Server-side per-turn completion on v2. Carries token + cost stats (`inputTokens`, `outputTokens`, `cacheReadTokens`, `cacheWriteTokens`, `reasoningTokens`, `toolCostUsd`), plus `model`, `persona`, `thinking`, `ttfbMs`, `totalMs`, `threadId`, `chat_experience: 'v2'`. Symmetric with `Dupe AI Chat Completed` on v1; lets the experiment compare cost/token cohorts.
   *
   *  ⭐ Also carries `rendered_product_count` (DUP-2221) — the count of products
   *  the shopper actually saw, model work plus all three controller injections
   *  (URL-paste search, Dupe Maxx card, compare card). This event fires from
   *  the controller after everything settles. Prefer it whenever the question
   *  is "did a grid render".
   *
   *  ⭐ Also carries `paste_zero_reason` (DUP-2221 item 2) when the DETERMINISTIC
   *  pre-executed URL search produced nothing — `host_unavailable` | `no_title` |
   *  `fallback_exhausted` | `parse_error`. Absent when that search produced a
   *  grid, and on non-paste turns. Measured 2026-09-09 at a ~20% give-up rate
   *  on pastes, split ~37% host-circuit / ~63% no-title, with
   *  `fallback_exhausted` at ~0% — the text fallback works when it runs.
   *
   *  ⚠️ It does NOT mean the shopper saw nothing. After the give-up the model
   *  still has its full tool set and can call `search` itself and recover, so
   *  this can be present on a turn that ended with a grid. ⭐ Shopper-facing
   *  failure is `paste_zero_reason IS NOT NULL AND rendered_product_count = 0`
   *  — both live on this event, so the join is free. The gap between those two
   *  populations IS the recovery rate, which is why the property is emitted on
   *  recovered turns instead of being suppressed.
   *
   *  ⛔ The paste denominator on THIS event is `pathway IN ('paste-link',
   *  'url-fallback')`. There is no `entry_type` field here (that is on the
   *  `$ai_*` events), so filtering it returns zero rows silently; and
   *  `pathway = 'paste-link'` alone excludes every give-up turn, because
   *  `classifyLegworkPathway` checks `urlFallbackUsed` before `entryType`. */
  BrainTurnCompleted = 'Brain Turn Completed',
  /** Server-side (EXP-A2 XL, mechanics M7/M8/M9): the post-grid next-step
   *  decider emitted a non-`none` offer, projected as a `nextStep` follow-up
   *  chip. Carries `tactic` (budget-tighten | compare | insight),
   *  `projectionForm: 'chip'`, `pathway` (text | paste | image), `threadId`,
   *  `chat_experience: 'v2'`. Acceptance is NOT a separate server event — it is
   *  the client-side chip tap (`Chats v2 Follow-Up Chip Tapped`, chipKind
   *  `nextStep`). Captured personless per the PostHog cost rules. */
  FollowOnOffered = 'Follow-On Offered',
  /** Server-side (EXP-A2, mechanic M5 rewired): the deterministic post-grid
   *  complementary hook shipped a non-empty "Completes the look" rail under a
   *  category-eligible (Fashion) grid. Carries `groupCount`, `category`,
   *  `threadId`, `chat_experience: 'v2'`. The offer denominator; acceptance is
   *  the client-side rail product click (`Dupe Result Clicked` with source
   *  `v2-complementary-product-list-card`). Captured personless per the PostHog
   *  cost rules (harness drives suppressed by safeCapture's `harness-`
   *  distinctId guard). */
  ComplementaryRailOffered = 'Complementary Rail Offered',
  /** Client-side (legwork Phase 1 — packages/chat-harness/docs/legwork/): a
   *  reconciled post-grid finding actually rendered on a product surface.
   *  Carries `findingCount`, `kinds`, `threadId`, `chat_experience: 'v2'`.
   *  SSE emission alone is not an impression; the web projection owns this. */
  LegworkVerificationShown = 'Legwork Verification Shown',
  /** Client-side: a shopper clicked a product card that carried one or more
   *  reconciled Legwork findings. Carries only bounded finding kinds/verdicts,
   *  count, result position, and threadId; product evidence remains out of
   *  analytics. This is the action numerator for the shown event above. */
  LegworkVerificationClicked = 'Legwork Verification Clicked',
  /** Client-side (legwork Phase 1): the grid-level honesty notice disclosed
   *  one or more deterministic hygiene removals. Carries `removedCount`,
   *  `threadId`, `assistantMessageId`, and `chat_experience: 'v2'`. Kept
   *  separate from `LegworkVerificationShown` so finding impressions and
   *  removal disclosures retain truthful denominators. */
  LegworkHygieneNoticeShown = 'Legwork Hygiene Notice Shown',
  /** Server-side (legwork next-step hook): a turn shipped evidence-gap chips.
   *  Carries `chipCount`, `gapCount`, and the `constraintIds` /
   *  `requiredEvidence` the chips name — those ids are the join key for the
   *  metric that actually matters, which is whether the NEXT turn closed the
   *  constraint a chip pointed at (a tap rate says only that the label read
   *  well). Personless; harness drives suppressed by safeCapture's `harness-`
   *  distinctId guard. */
  LegworkEvidenceGapChipsOffered = 'Legwork Evidence Gap Chips Offered',
  /** Client-side: a shopper tapped a product named in the assistant's PROSE on a
   *  PRESENTED (`showProducts`) turn, which scrolls to and highlights that
   *  product's card instead of opening the retailer. Carries `threadId`,
   *  `chat_experience: 'v2'`, and `found` — false when the anchor pointed at a
   *  card that is not on screen, which is the failure mode worth watching (the
   *  chip promises a jump it can't make). The paired click-out is still measured
   *  by `Dupe Result Clicked` on the card itself. */
  BrainProseProductAnchorClicked = 'Brain Prose Product Anchor Clicked',
  /** Server-side, personless canary record for every completed Legwork turn
   *  and authorized Legwork request that resolved to another profile. Carries
   *  profile/pathway, hook outcome, bounded evidence-tool counters, mismatch,
   *  stale-drop, latency, cost, and error/timeout counts. No prompt, product
   *  title, receipt, URL, or finding detail is included. */
  LegworkVerificationRun = 'Legwork Verification Run',
  /** Server-side (EXP-A2 XL, mechanic M10): the post-grid context-pill
   *  generator emitted a non-empty question set for a paste/image turn.
   *  Carries `questionCount`, `pathway` (paste | image), `threadId`,
   *  `chat_experience: 'v2'`. The offer denominator; acceptance is the
   *  client-side `Chats v2 Context Pills Answered`. Captured personless per the
   *  PostHog cost rules (harness drives suppressed by safeCapture's `harness-`
   *  distinctId guard). */
  ContextPillsOffered = 'Context Pills Offered',
  /** Server-side per-turn completion on v1 (dupe-agent). Carries `inputTokens`, `outputTokens`, `totalTokens`, `model`, `ttftMs`, `totalMs`, `threadId`, `chat_experience: 'v1'`. Symmetric with `Brain Turn Completed`. */
  DupeAIChatCompleted = 'Dupe AI Chat Completed',
  SendTradeExpertMessage = 'Send Trade Expert Message',
  SendExpertResults = 'Send Expert Results',
  DupeUrlCopied = 'Dupe URL Copied',
  FeaturedDupeCardClicked = 'Featured Dupe Card Clicked',
  DupeProductClicked = 'Dupe Product Clicked',
  DupeResultClicked = 'Dupe Result Clicked',
  DupeAssessmentClicked = 'Dupe Assessment Clicked',
  DupeFeedbackSubmitted = 'Dupe Feedback Submitted',
  HowItWorksClicked = 'How It Works Clicked',
  RemovePhotoClicked = 'Remove Photo Clicked',
  ResultsErrorReload = 'Results Error Reload',
  ResultsErrorShown = 'Results Error Shown',
  DupeShareClicked = 'Dupe Share Clicked',
  DupeFeedbackClicked = 'Dupe Feedback Clicked',
  /** Render-time redirect RESOLUTION, not a human click (`event_type` is always
   *  `'resolution'`). `link_clicks` rows are pre-resolved before any click, so
   *  this over-counts real clicks ~3×. For genuine clicks use
   *  `LinkClickConfirmed`. */
  PageRedirect = 'Page Redirect',
  /** DUP-1713: a CONFIRMED human click-out — emitted server-side when the
   *  `sendBeacon` to `POST /api/redirect/confirm` flips a `link_clicks` row from
   *  `resolution` to `click`. Fires at most once per row. This is the only
   *  honest click signal in PostHog; `PageRedirect` is resolutions. Carries the
   *  row's `src` (e.g. `instant-search-broadcast` = the "Also search" checkbox)
   *  plus the router_* decision props, so click-outs are sliceable by surface,
   *  partner, and experiment arm without joining Postgres. */
  LinkClickConfirmed = 'Link Click Confirmed',
  /**
   * Fired when the /redirect interstitial fails to resolve a buy link — the
   * only reliable failure signal for that page (RUM is 0%-sampled, so the
   * prior logger.error was invisible and the 2026-06-09 redirect P1 went
   * undetected). Stages: validate | resolver_fetch | redirect; the hard-fail
   * P1 shape is stage:'redirect', recovered:false, hadToken:true. To be
   * monitored by a PostHog rate alert — post-merge follow-up DUP-986.
   */
  RedirectInterstitialFailed = 'Redirect Interstitial Failed',
  /** Sampled (5%) result of the redirect page's client-side Strackr wrap:
   *  wrapped | unchanged | timeout (script blocked or slow) | missing. */
  RedirectStrackrOutcome = 'Redirect Strackr Outcome',
  OurPicksMoreDealsClicked = 'Our Picks More Deals Clicked',
  WayfairBannerClicked = 'Wayfair Banner Clicked',
  ClearanceItemClicked = 'Clearance Item Clicked',
  ExtensionUninstallFeedback = 'Extension Uninstall Feedback',
  ExtensionReinstall = 'Extension Reinstall',
  ExtensionUninstallReasonClicked = 'Extension Uninstall Reason Clicked',
  InputSuggestionClicked = 'Input Suggestion Clicked',
  InputSuggestionsDisplayed = 'Input Suggestions Displayed',
  PushCampaignNotificationSent = 'Push Campaign Notification Sent',
  SafariNativeStylePushSent = 'Safari Native Style Push Sent',
  SafariNativeStylePushShown = 'Safari Native Style Push Shown',
  SafariNativeStylePushTapped = 'Safari Native Style Push Tapped',
  SimilarFindsEmailSent = 'Similar Finds Email Sent',
  UnclaimedCoinsEmailSent = 'Unclaimed Coins Email Sent',
  UnclaimedCoinsPushSent = 'Unclaimed Coins Push Sent',
  ExtensionSidePanel = 'Extension Side Panel',
  ExtensionSidePanelReload = 'Extension Side Panel Reload',
  ExtensionSidePanelOpen = 'Extension Side Panel Open',
  ExtensionSidePanelExpanded = 'Extension Side Panel Expanded',
  ExtensionSidePanelCopied = 'Extension Side Panel Copied',
  ExtensionSidePanelSelectImage = 'Extension Side Panel Select Image',
  ExtensionSidePanelSearch = 'Extension Side Panel Search',
  ExtensionSidePanelFindSimilar = 'Extension Side Panel Find Similar',
  /** The side panel's Dupe home screen showed, on a page with nothing to check.
   *  Once per panel life per (tab, reason). Properties: `reason` (browser,
   *  webstore, resting, or unknown = the url is hidden from the extension,
   *  usually a browser page such as the new tab), `has_previous_results`.
   *  Never carries a url or host. PostHog-only (`skipGA: true`). */
  ExtensionSidePanelHomeViewed = 'Extension Side Panel Home Viewed',
  /**
   * Fired by the WEB app (inside the panel's iframe) the moment it tells the
   * extension it may reveal the results page. Carries how long the shopper
   * waited and, when the deadline forced it, which condition was still
   * blocking. See `apps/web/src/lib/sidepanelHandoff.ts`.
   */
  SidepanelResultsHandoff = 'Sidepanel Results Handoff',
  ResultsExtensionTabPressed = 'Results Extension Tab Pressed',
  ResultsExtensionWebStoreLinkClicked = 'Results Extension Web Store Link Clicked',
  SponsoredResultImpression = 'Sponsored Result Impression',
  SponsoredResultClicked = 'Sponsored Result Clicked',
  /** DUP-2093 — fired when the Best Buy AMP swap (`dedupeOrganicForBestBuyAmp`
   * in `@dupe/common/admarketplace`) actually drops an organic Best Buy row
   * in favor of a matching AMP ad. Requested in review (Joe21): there was no
   * way to see in production whether/how often the swap fires. Properties:
   * `surface` (which of the swap's call sites fired) and `dropped_count`.
   * PostHog-only (`skipGA: true`) — this is an internal visibility signal,
   * not a lifecycle/marketing event. */
  AmpBestBuySwap = 'AMP Best Buy Swap',
  /** Viewability-gated organic-result impression (DUP-585). Fired when a result
   * card has been continuously visible above the configured threshold for the
   * configured dwell duration. One event per (card, mount). Properties:
   * `hash`, `position`/`resultIndex`, `shop_key`, `source`, `pick`, `tier`,
   * `sort_criteria`, `ranker_version`, `dwell_ms`, `is_exploration`, optional
   * `threadId`. */
  ResultViewed = 'Result Viewed',
  /** Interleaving grid impression (DUP-1776 / DUP-1022). Fired once per rendered
   * interleaved grid, keyed on the batch id, from `ProductRow`. This is the
   * RENDER-time denominator for the win-rate readout: `search_routing_events`
   * counts batches minted at SEARCH time, so a brain turn that errors or asks a
   * clarifying question inflates it with a batch that never reached the shopper.
   * `uniqExact(ibid)` over this event counts only grids that actually rendered.
   * Tinybird-only (`analytics_events`, `action = 'Interleave Grid Shown'`);
   * interleave attribution is stripped on persistence, so a rehydrated grid
   * never fires it. Properties: `ibid` (batch id), `threadId`, `n` (grid size),
   * `n_a`/`n_b` (per-arm tile counts — an all-one-arm grid signals a
   * served-order regression). */
  InterleaveGridShown = 'Interleave Grid Shown',
  /** Server-side per-`/api/parse` capture. Properties: `outcome` (`success`|`cache_hit`|`failure`|`error`|`invalid_url`|`circuit_breaker`), `mode` (`api`|`worker`), `mode_source` (`env`|`default`), `strategy`, `cache_status` (`HIT`|`MISS`|`CIRCUIT_BREAKER`|null), `shop_key`, `duration_ms`, `url_host`, `source`, `hash`, `status_code`, `enrichment_dispatched` (boolean — true when the controller fired `api/enrich-parse-result-from-hash`), `request_id` (UUID propagated to the worker via `X-Request-ID` so `analyze_parse` can pivot from event → DataDog logs across both api + worker), `cache_veto_reason` (only when a *titled* cached row existed: `none` when it was served, otherwise a `+`-joined list of `refresh`|`invalid_image`|`no_image`|`stale`|`include_raw` — DUP-2374), `image_probe_ms` (hot-path image-reachability probe time, only when a probe ran). */
  ProductScrape = 'Product Scrape',
  /** Heart or "Not for me" saved on a fragrance chat list card row (DUP-3133). Properties: `reaction` (`heart`|`unheart`|`not_for_me`|`undo_not_for_me`), `engine` (`new` since DUP-3273; `current` on older events, the recommender that ranked the list), `engine_via` (`default` since DUP-3273; `flag`|`env`|`staff`|`fallback` from the A/B; null with `engine`), `item_id` (fragrance_items id), `list_position` (1-based row on the card), `thread_id`, `product_name`, and on `not_for_me` the tapped chip ids as `not_for_me_reasons` (never their typed words). Fires once the save lands. */
  /** "Take the fragrance quiz" tapped: the quiz from question 1. Properties: `surface` (`next_steps`|`bottle_card`), `thread_id`. Funnel start for `Fragrance Quiz Step Viewed` and `Fragrance Quiz Ended`. */
  FragranceQuizStarted = 'Fragrance Quiz Started',
  /** A step of a picture-quiz round came on screen (newest round only). Properties: `round` (1–3), `step` (1-based), `steps` (in this round), `question_id`, `thread_id`. The last step viewed without a `finished` end is where they dropped off. */
  FragranceQuizStepViewed = 'Fragrance Quiz Step Viewed',
  /** A picture-quiz round or the worn bottles picker ended. Properties: `kind` (`quiz_round`|`worn_picker`), `outcome` (`finished`|`left`; the picker also `auto_built` when its deck ran out), `thread_id`; a round adds `round`, `furthest_step`, `steps`; the picker adds `answered`. `left` fires when the round or picker leaves the screen unsent (they typed something else, or started over); a closed tab sends nothing, so read drop-off off the last `Fragrance Quiz Step Viewed` too. */
  FragranceQuizEnded = 'Fragrance Quiz Ended',
  FragranceListCardReaction = 'Fragrance List Card Reaction',
  /** A Love, Own, Want to try or Not for me tap on any fragrance bottle in chat, once its save lands. Properties: `action` (`love`|`unlove`|`own`|`unown`|`want`|`unwant`|`not_for_me`|`undo_not_for_me`), `surface` (`ranked_list`|`bottle_card`|`social_post`|`my_bottles`), `item_id` (fragrance_items id), `thread_id`, `product_name`, and `list_position` (1-based) on a list card row. On list cards `Fragrance List Card Reaction` still fires beside it for the engine A/B. */
  FragranceCardAction = 'Fragrance Card Action',
  /** A tab picked on an opened fragrance bottle in chat (DUP-3094): not the Details tab it opens on. Properties: `tab` (`details`|`reviews`|`similar`), `item_id` (fragrance_items id), `thread_id`, `product_name`. */
  FragranceBottleTabViewed = 'Fragrance Bottle Tab Viewed',
  /** A bottle in an opened bottle's Similar tab tapped (DUP-3269), once its card ask ends. Properties: `outcome` (`opened` = shown in the bottle panel | `missing` = not one of our rows | `failed` = the ask failed or was rate limited), `item_id` (the tapped bottle's fragrance_items id), `from_item_id` (the bottle whose Similar tab it was in), `similar_position` (1-based row), `closeness` (the row's label), `thread_id`, `product_name`. */
  FragranceSimilarBottleOpened = 'Fragrance Similar Bottle Opened',
  /** "Something looks wrong?" sent from a fragrance bottle card in chat, signed in or out. PostHog only (`skipGA`): `note` is the shopper's own words. Properties: `fragrance_item_id` (fragrance_items id), `problem` (`wrong_picture`|`wrong_details`|`not_this_fragrance`|`wrong_price`|`other`), `note` (up to 300 chars, or null), `surface` (`chat_card` = the card in the chat | `bottle_sheet` = the bottle's details sheet | `buy_panel` = the Where to buy panel). Once per bottle per browser session. ⚠️ snake_case name, unlike its neighbours, by request. */
  FragranceBottleFlagged = 'fragrance_bottle_flagged',
  /** Server, once per fragrance answer, after the last model attempt (tracking plan §8a). One row per answer, keyed on `message_id` (the persisted assistant message id that `link_clicks.metadata.message_id` and the client events carry). Properties: `thread_id`, `message_id`, `is_staff` (verified admin email, from the token; filter on this, not the Internal Users cohort), `is_harness`, `turn_index` (shopper messages in the chat, this one included), `answers_so_far` (advisor questions answered in the chat, this turn included: Reach@4 reads `<= 4`), `answered_question`, `tool`, `route`, `list_shown`, `rows` (ordered `{item_id, verdict, fit, priced}`, at most 10), `rows_shown`, `blind_buys_shown`, `engine`, `audience` (`self`|`gift`), `post_cap`, `geo_blocked`, `model`, `latency_ms`, `cost_usd`. No PII. ⚠️ snake_case like the other server fragrance events. */
  FragranceTurnCompleted = 'fragrance_turn_completed',
  /** A fragrance answer's ranked list first at least half on screen: once per answer per page session, however often it re-renders, scrolls back or remounts (tracking plan §8b). Properties: `thread_id`, `message_id` (joins `fragrance_turn_completed`), `item_ids` (ordered as shown), `rows_shown`, `blind_buys_shown`, `engine`, `restored` (a saved or shared chat reopened: filter these out to count lists seen when new). Ids only. */
  FragranceListShown = 'Fragrance List Shown',
  /** A fragrance bottle's details sheet opened in chat. Properties: `thread_id`, `message_id` (the answer it was opened from; null before its id lands), `item_id`, `list_position` (1-based row when opened from a list, else null), `surface` (`row` = a list row | `photo` = a bottle card's photo or name | `mention` = a bottle named in the reply | `my_bottles` | `social_post` | `similar` = a Similar tab bottle | `buy` = a bottle card's Buy | `find_prices` = a bottle card's Find prices), `engine`. */
  FragranceBottleOpened = 'Fragrance Bottle Opened',
  /** A fragrance bottle's Where to buy shops opened. Properties: `thread_id`, `message_id`, `item_id`, `list_position` (1-based, or null), `offers_count`, `has_price`, `has_department_store` (one of the DUP-3397 department stores, `@dupe/common/fragranceDeptStores`, is among them), `trigger` (`buy` = a Buy opened the shops drawer, or opened or scrolled the details sheet to its shops | `scroll` = the shopper scrolled the sheet's shops into view). Per details sheet: `buy` at most once, even after a `scroll`; `scroll` at most once and never after a `buy`. So a sheet sends `scroll` then `buy`, or `buy` alone, or `scroll` alone. `engine`. Never for a Find prices tap, which has its own event. */
  FragranceWhereToBuyOpened = 'Fragrance Where To Buy Opened',
  /** "Find prices" tapped on an unpriced fragrance bottle; fires again with `results_count` when that search ends. Properties: `thread_id`, `message_id`, `item_id`, `list_position` (1-based, or null), `results_count` (null on the tap, and when the search told nothing), `outcome` (null on the tap; `found`|`none`|`still_looking`|`refused`), `engine`. */
  FragranceFindPricesClicked = 'Fragrance Find Prices Clicked',
  // ── Dupe Maxx ────────────────────────────────────────────────────────────
  /** Card rendered in chat (first mount). Carries `original_id`, `original_name`,
   *  `original_brand`, `dupe_count`, `variant` (A|B). */
  DupeMaxxCardViewed = 'Dupe Maxx Card Viewed',
  /** Pick-row card tapped → opens detail panel. */
  DupeMaxxPickClicked = 'Dupe Maxx Pick Clicked',
  /** "View all N options →" button tapped → opens All Products modal. */
  DupeMaxxViewAllClicked = 'Dupe Maxx View All Clicked',
  /** Sort tab changed inside All Products modal. Carries `sort`. */
  DupeMaxxModalSortChanged = 'Dupe Maxx Modal Sort Changed',
  /** Card checked/selected in modal grid. Carries `selected_count_after`. */
  DupeMaxxCardSelected = 'Dupe Maxx Card Selected',
  /** Card unchecked/deselected in modal grid. Carries `selected_count_after`. */
  DupeMaxxCardDeselected = 'Dupe Maxx Card Deselected',
  /** "Compare N vs. [Original]" CTA tapped. Carries `selected_count`, `selected_dupe_ids`. */
  DupeMaxxCompareSubmitted = 'Dupe Maxx Compare Submitted',
  /** Card tapped inside the All Products modal grid (opens detail panel). */
  DupeMaxxModalCardClicked = 'Dupe Maxx Modal Card Clicked',
  /** Original product card clicked (Variant B) → opens product URL. */
  /** Detail side panel opened. */
  DupeMaxxDetailOpened = 'Dupe Maxx Detail Opened',
  /** Detail side panel closed. Carries `close_method` (button|escape). */
  DupeMaxxDetailClosed = 'Dupe Maxx Detail Closed',
  /** "Buy from [Brand]" CTA tapped → outbound affiliate link. */
  DupeMaxxBuyClicked = 'Dupe Maxx Buy Clicked',
  /** Share button tapped. Carries `method` (native|clipboard). */
  DupeMaxxShareClicked = 'Dupe Maxx Share Clicked',
  /** "Other options" card tapped in detail panel. Carries `from_dupe_id`. */
  DupeMaxxOtherOptionClicked = 'Dupe Maxx Other Option Clicked',
  /** "View comparison" / "Hide comparison" pill under the comparison table
   *  toggled. Carries `expanded` (the state after the tap) and `row_count`
   *  (grade + scale + spec rows the pill reveals). */
  DupeMaxxComparisonExpanded = 'Dupe Maxx Comparison Expanded',
  /** "More researched picks" rail entered the viewport (≥50% visible for 1s,
   *  once per mount). Carries `thread_id`, `source_slug` (original on screen),
   *  `product_count`. */
  DupeMaxxRailViewed = 'Dupe Maxx Rail Viewed',
  /** Rail product card tapped → submits its trigger keyword in-thread.
   *  Carries `slug`, `keyword`, `position`, `thread_id`, `source_slug`. */
  DupeMaxxRailCardClicked = 'Dupe Maxx Rail Card Clicked',
  /** "What product should we research next?" form submitted. Carries
   *  `suggestion`, `success`, `thread_id`, `source_slug`. */
  DupeMaxxSuggestSubmitted = 'Dupe Maxx Suggest Submitted',
  /** `/max` discovery index rendered. Carries `product_count`. Fires once
   *  per mount — the page is the whole viewport, so no dwell gate. */
  DupeMaxxIndexViewed = 'Dupe Maxx Index Viewed',
  /** `/max` index card clicked → navigates to the chat query that routes
   *  to the guide. Carries `slug`, `keyword`, `position`, `category`. */
  DupeMaxxIndexCardClicked = 'Dupe Maxx Index Card Clicked',
  // ── Site navigation ──────────────────────────────────────────────────────
  /** A link in the header, desktop nav, slide-out menu or footer was clicked
   *  (web has no autocapture, so these were invisible). Carries `location`
   *  ('header'|'desktop-nav'|'menu'|'footer'), `href`, `label`, `from_route`
   *  (the Pages Router route pattern; on App Router pages a fixed bucket
   *  like `/reports/[...]` — never a raw path with ids). */
  NavLinkClicked = 'Nav Link Clicked',
  // ── /steals Drop Board ───────────────────────────────────────────────────
  /** Deal card clicked → outbound affiliate buy link. Carries `product_id`,
   *  `name`, `brand`, `retailer`, `sale_price`, `msrp`, `pct_off`,
   *  `currency`, `rank` (1-based position in visible list), `rarity`
   *  ('Gem'|'Rare'|'Score'), `department`, `buy_url`. */
  StealsDealClicked = 'Steals Deal Clicked',
  /** Buy link on a fragrance house page (/products/fragrance/brands/<slug>). Carries
   *  `item_id`, `house`, `shop`, `price`, `page`, `position` (1-based on the page). */
  FragranceHouseBuyClicked = 'Fragrance House Buy Clicked',
  /** A bottle tapped on a fragrance house page, to open its details panel. Carries `item_id`,
   *  `house`, `page`, `position` (1-based on the page, as on Buy Clicked), and `result`:
   *  `opened`, `missing` (no card for this bottle or viewer) or `failed` (the request failed), and
   *  `via`: `tap` on the page, or `link` (a `/brands/<house>/<bottle>` URL, Back or Forward).
   *  `position` is null when the bottle isn't on the page. */
  FragranceHouseBottleOpened = 'Fragrance House Bottle Opened',
  /** A pick in the fragrance houses index search box. Carries `kind` (`house` | `bottle`), `id`
   *  (house slug or bottle id), `position` (1-based in the list), `query_length`, and for a bottle
   *  `result` (`opened` | `missing` | `failed`, as on Bottle Opened). */
  FragranceHousesSearchPicked = 'Fragrance Houses Search Picked',
  /** Coupon ticket clicked → outbound affiliate offer link. Carries
   *  `coupon_id`, `merchant`, `vertical`, `has_code`, `is_urgent`, `offer`. */
  StealsCouponClicked = 'Steals Coupon Clicked',
  /** Coupon code copied (tap-to-copy, no navigation). Fired so a copy-only
   *  visit isn't counted as a Plausible bounce. Carries `coupon_id`,
   *  `merchant`, `vertical`. */
  StealsCouponCodeCopied = 'Steals Coupon Code Copied',
  /** Sponsored brand section (top of /steals) scrolled into view. Carries
   *  `brand`, `product_count`. Fires at most once per page view. */
  StealsSponsorImpression = 'Steals Sponsor Impression',
  /** Sponsored brand section clicked → outbound advertiser click-out via
   *  /redirect. Carries `brand`, `name` (product, or null for the "View the
   *  collection" header link), `position` (0-based tile index, null for the
   *  header link), `href`. */
  StealsSponsorClicked = 'Steals Sponsor Clicked',
}

export enum FeatureFlags {
  Turkee = 'turkee', // Deep research mode with archetype selector
  /** DUP-1611: gates the weekly ingestion index-health report (Inngest cron). Consumed server-side in apps/api. Absent/false → weekly run no-ops. Must be a plain boolean (not cohort-targeted) for reliable server eval. */
  WeeklyIndexHealthReport = 'weekly-index-health-report',
  /** DUP-1806: gates the `marqo_outbox` dispatcher cron (`dispatch-marqo-outbox`). Consumed server-side in apps/api. Absent/false → the tick no-ops, which is the SAFE default: turning this on before the `catalog/marqo-outbox.product` handler exists hands out work nothing completes. Must be a plain boolean (not cohort-targeted) for reliable server eval. */
  MarqoOutboxDispatcher = 'marqo-outbox-dispatcher',
  ClipReranking = 'clip-reranking', // CLIP-based visual similarity re-ranking
  GiftGuides = 'gift-guides',
  AIAnalysis = 'ai-analysis',
  /** DUP-2387 — renders the price-level bar at the top of the EXTENSION SIDE
   *  PANEL's product snapshot block, fetched automatically on load via the
   *  single-step `POST /api/ai/product/price-analysis` workflow.
   *
   *  ⚠️ This is a rollout dial AND the kill switch, not a cosmetic gate. Turning
   *  it on makes every side-panel product view fire a Gemini agent call.
   *  Measured 2026-09: ~18,500 monthly `/chats/[hash]/sidepanel` pageviews, 87%
   *  of them distinct URLs, so expect ~16k added model runs/month on top of the
   *  ~12,200 the price step ALREADY does (11,857 of those inside `urlWorkflow`)
   *  — about 2.3x on the step, +26% on url-workflow agent volume.
   *  Do NOT size this off `Extension Side Panel Open`: that event is a bare
   *  mount effect in the panel shell, fires on non-product pages and on every
   *  reload, and carries no product context.
   *  Cost, MEASURED 2026-09-23 over 62 runs of the exact request the panel makes
   *  (52 real product pages): ~$0.0015/run on average — ~$24/month at 16k runs,
   *  less once the 24h `workflow_runs` cache absorbs repeats. It is
   *  TOKEN-dominated (~1,150 in / ~120 out at $0.30/M and $2.50/M), not
   *  grounding-dominated: search fired on 1 run in 62. Most runs cost ~$0.0006;
   *  reading the page pushes one to ~$0.007 (7 of 50), and the single searching
   *  run hit $0.015. One model call per run, always gemini-3.1-flash-lite, no
   *  fallbacks. An earlier estimate here said $225-670/month on an assumed 1-3
   *  searches per run — that was inference from latency, and it was wrong by
   *  more than an order of magnitude.
   *  Still ramp it and watch spend and the `Sidepanel Price Level Shown`
   *  coverage event: the cost is small but the volume is real, and this is the
   *  only instant off-switch — a code revert needs a deploy.
   *
   *  ⚠️ Cost is the ONLY thing this flag controls, and it fails closed — an
   *  absent, draft or unresolved flag reads `undefined` from `useIsFeatureOn`,
   *  which is falsy, so the component never mounts and no request fires. Do NOT
   *  "improve" this with a second, inverted disable-flag: that would fail OPEN,
   *  billing on every eval failure. See docs/references/feature-flags.md §7.
   *  ⛔ DO NOT ARCHIVE when it sits at 0% — it looks stale to the weekly flag
   *  audit, and archiving silently turns the off-switch into a no-op.
   *
   *  It only ADDS the price bar. It cannot bring the "Research In Detail"
   *  button back: the extension side panel never shows that sheet, which is
   *  unconditional in `ProductDetails` and not flag-controlled. So at 0% the
   *  panel shows neither — deliberate, per the 2026-09-22 product call. The
   *  sheet was only ever reachable there for panel hashes carrying
   *  `source = 'product-url'` (~1 in 6) and was never meant to be on that
   *  surface. Read in `ProductDetails`; the component it mounts does not
   *  re-check it. */
  SidepanelPriceLevel = 'sidepanel-price-level',
  EarlyAccessToggle = 'early-access-toggle',
  RewardsProgram = 'rewards-program',
  AppPreorderModal = 'app-preorder-modal',
  Web3Wallet = 'web3-wallet',
  BnplSurvey = 'bnpl-survey', // BNPL survey on DupeResults.tsx
  RestyleRoom = 'restyle-room',
  RestyleObjectDetection = 'restyle-object-detection',
  PaywallDrawer = 'paywall-drawer',
  PaywallStripePay = 'paywall-stripe-pay',
  OnboardingPaywallStep = 'onboarding-paywall-step',
  PaywallStripeApplePay = 'paywall-stripe-apple-pay',
  ResultsSurvey = 'results-survey',
  LudicrousSpeed = 'ludicrous-speed', // experiment (https://us.posthog.com/project/71992/experiments/37842)
  DesktopExtensionWall = 'desktop_extension_wall',
  DesktopExtensionBlockContent = 'desktop_extension_block_content',
  HomeInputSuggestions = 'home_input_suggestions',
  DesktopExtensionBlockContentMobileSafari = 'desktop_extension_block_content_mobile_safari',
  SupportsSheetCsvFiles = 'supports_sheet_csv_files',
  EmailPreferences = 'email-preferences',
  RestyleSessionRecording = 'restyle-session-recording', // Controls session recording for restyle users
  InstagramPaidUsers = 'instagram-paid-users', // Controls session recording for Instagram paid users
  ResultsPageExtensionPreview = 'results-page-extension-preview',
  ResultsPageExtensionPreviewSidepanel = 'results-page-extension-preview-sidepanel',
  /** Gates the Refine drawer on the sandbox brain chat (Google Shopping filter groups above the input). */
  BrainRefinements = 'brain-refinements',
  /** Gates the deep-research tool in v2 brain chat (DUP-457 spike). Internal users only; 3/user/day rate limit. */
  ChatDeepResearch = 'chat-deep-research',
  /** Gates the apply-to-grid `rankResultSet` tool in v2 brain chat. Ranks products the user already has on screen via Haiku 5.5, with Gemini grounded enrichment when the enrichment gate passes. Internal users only; ~$0.037/call enriched, ~$0.001 when the gate skips Gemini. */
  ChatRankResultSet = 'chat-rank-result-set',
  /** Gates the `lookupDupeKnowledge` tool (and `lookupFragrance`, live Fragella + Fragplace scent facts) on the v3 brain profile — a lookup over Dupe Maxx guides (and later Dupe Research) that runs alongside the keyword-triggered `showDupeMaxx`. Server-evaluated (batched, no exposure event); harness drives override with `expDupeKnowledge`. docs/features/active/brain-dupe-knowledge/. */
  BrainDupeKnowledge = 'brain-dupe-knowledge',
  /** Gates the `lookupImages` brain tool on the v3 profile: a strip of web photos (Google Images via SearchAPI, SafeSearch strict) shown above the answer when a picture helps, e.g. the character a costume is of. ~$0.0015/search, cached 24h. Server-evaluated (batched, no exposure event); harness drives override with `expImageLookup`. docs/features/active/brain-image-lookup/. */
  BrainImageLookup = 'brain-image-lookup',
  // FOLDED into `brain-fragrance-chat` (below): `brain-identify-fragrance`
  // (921572), `brain-fragrance-router` (926072), `brain-fragrance-shortlist`
  // (926071) and the never-read `brain-fragrance-lookup` (919003). The code
  // reads only the master; the four were deleted in PostHog on 2026-11-03.
  /** Shadow advisors (owner: Ramin): after each brain turn, GPT-6.1 Sol and Claude Opus 5.5 each answer the newest message blind (no Dupe prompts) and review the agent's turn against what the engine can do, in a background Inngest job; scores, notes and both answers land on the turn's Langfuse trace. ~$0.30-0.60 a turn, so Internal Users only. Server-evaluated in the brain's batched flag call (no exposure event). apps/api/src/lib/brain/shadowAdvisors/. */
  BrainShadowAdvisors = 'brain-shadow-advisors',
  /** Master switch for the whole fragrance chat (owner: Ramin). Off/absent → no fragrance tool is offered (`lookupFragrance`, `identifyFragrance`, `fragranceShortlist`) and the fragrance router never runs; on → `identifyFragrance`, `fragranceShortlist` and the router are on (their old per-tool flags are folded in), and `lookupFragrance` still also needs `brain-dupe-knowledge`. Everything the fragrance chat does is on with the master; its JSON payload carries only six off-by-default switches, `{"parts": {"vendorPool": true}}` — one of houseClone, celebrityPhotos, catalogGrowth, vendorPool, purchaseStep, dupeMaxxCard — on only with the master on and the payload setting it `true`; any other name is ignored (apps/api/src/lib/brain/fragranceChatSwitch.ts). Server-evaluated in the brain's batched flag call (no exposure event); harness drives override with `expFragranceChat` / `expFragranceParts`. docs/features/active/fragrance-blitz/v3-integration/ (chunk C04). Staff (C05): the value `staff` (multivariate, served to the Internal Users cohort) also exempts the viewer from the fragrance US-only check; `true` / `enabled` / `on` are on but not staff. Harness: `expFragranceStaff`. */
  BrainFragranceChat = 'brain-fragrance-chat',
  /** Offers `readSocialPost` on every chat profile (fragrance chat or not): it reads a pasted YouTube / TikTok / Instagram / Reddit post's title and description, says what the post is about, and for a fragrance post shows the bottles it names as a card (frag-social-links, DUP-3087, decisions D6). Off by default; staff first. Server-evaluated in the brain's batched flag call (no exposure event); harness drives override with `expFragranceSocialLinks`. docs/features/active/frag-social-links/. */
  FragranceSocialLinks = 'fragrance-social-links',
  // RETIRED 2026-10-22: `fragrance-rec-engine` (970849) — the fragrance
  // recommender A/B (DUP-3133). The `new` engine serves everyone (DUP-3273).
  // RETIRED 2026-09-21: `brain-expa-multiturn` (763428) — EXP-A2 multi-turn
  // mechanics. Experiment 384133 concluded 2026-09-01 at 100% `control` / 0%
  // `test`, so the follow-on offer policy lost and served nobody. #5173
  // hardcoded `followOnPolicyEnabled` to `false` in the brain prompt
  // controller; the `refineGrid` tool, next-step decider and context pills are
  // KEPT and still threaded, they simply no longer sit behind a flag. Its
  // `EC_ALLOWLIST` entry in `scripts/posthog-flag-audit/config.ts` is gone with
  // it. Archived 763428 (DUP-2462); deleted in PostHog on 2026-11-03.
  /** v3 canary — routes a brain turn to the `v3` profile (minimal prompt, 5 of
   *  20 tools, Sonnet 5). `test` enables; `control`, absent, or an unresolved
   *  flag leave prod behavior unchanged.
   *
   *  ⏸️ PAUSED (2026-09-03, flag `active: false` in PostHog — no code change,
   *  no deploy). It briefly ran internal-only (`email icontains @dupe.com`),
   *  but staff already have a working path to v3 via the admin profile
   *  override (`body.profile`, gated on `isAdminCaller || req.isHarnessDrive`
   *  in `resolveActive.ts`) — a second, flag-based way to get the same
   *  profile for the same people was redundant. Next step is NOT re-enabling
   *  the internal-only condition; it's replacing it with a real rollout
   *  percentage (1–5%) once that's ready. All the plumbing below (client hook,
   *  header pair, allowlist, server routing) stays as-is for that — flip
   *  `active: true` and/or reshape the flag's condition groups in PostHog when
   *  it's time, no code or deploy needed either way.
   *
   *  ⭐ Rollout lives in PostHog, so widening the audience is a slider, not a
   *  deploy. This flag REPLACED a bespoke in-code assignment registry
   *  (`profiles/experiments.ts`, deleted 2026-09-03) — there is now ONE way to
   *  split brain traffic, and this is it.
   *
   *  Shape: 50/50 control/test over all web chat, anonymous included. `test`
   *  serves v3 only to TEXT-first threads — a thread whose first turn is a
   *  pasted link or an image stays on base in both arms (`isTextEntryTurn` in
   *  `./chatPathway`, shared by the server gate and the client's attribution).
   *  Staff can still force v3 through the admin profile override.
   *
   *  ⚠️ CLIENT-evaluated and forwarded to the brain on the
   *  `x-brain-flag-key` / `x-brain-variant` header pair (`useBrainV3CanaryVariant`
   *  → BrainChat). Do NOT "simplify" this to a server-side read: the brain's
   *  `getFeatureFlagsBatch` forwards no person properties for anonymous
   *  `tempUserId` traffic, so any property-conditioned release resolves to its
   *  default for them. Next's middleware evaluates against the real PostHog
   *  person and bootstraps the map into a cookie, which is what makes the
   *  client answer correct AND synchronous on a cold first turn.
   *
   *  ⚠️ Only web sends the header. The mobile app and the extension sidepanel
   *  hit the brain without it and therefore always run base — deliberate for a
   *  canary, but it means this flag can never be a whole-traffic rollout dial
   *  until those clients forward it too.
   *
   *  ⚠️ Harness drives ignore the header (they pick arms via `body.profile`),
   *  so a flag flip can never contaminate a bench control arm. */
  BrainV3Canary = 'brain-v3-canary',
  /** Live model test inside v3 (`resolveV3ModelArm`, apps/api profiles):
   *  `control` = Sonnet 5, `haiku-5-5` = Haiku 5.5 at effort medium.
   *  SERVER-evaluated in the brain's flag batch, unlike `brain-v3-canary`.
   *  That is safe only for a plain percentage rollout: the server and the web
   *  middleware hash the same distinct id (`authUserId || tempUserId`), but the
   *  server forwards no person properties for anonymous traffic.
   *
   *  ⚠️ Keep the release free of person-property conditions, and keep
   *  evaluation runtime "all" (a client-only flag misses the bootstrap, so
   *  web click events would go out without `$feature/brain-v3-model`). */
  BrainV3Model = 'brain-v3-model',
  /** v3's opening line: one or two sentences before the first search, sent
   *  as its own `opening` frame so the web shows it while the search runs and
   *  keeps it out of the paragraph the product cards follow
   *  (`resolveV3OpeningLine`, apps/api profiles). Boolean, SERVER-evaluated in
   *  the brain's flag batch; the same plain-percentage rule as
   *  `brain-v3-model` applies. */
  BrainV3OpeningLine = 'brain-v3-opening-line',
  // RETIRED 2026-07-20: `exp-multiturn-fashion` — the "Completes the look"
  // rail is owned by agent profiles now (profile.hooks.complementary on the
  // fashion + legwork manifests). Deleted in PostHog on 2026-11-03.
  // RETIRED 2026-09-18: `amazon-redirect-experiment` (199287) — collapsed to
  // 100% `test` / 0% `control` in PostHog, and the FlexOffers-vs-AdMedia branch
  // in `apps/api/src/lib/redirect/networks/amazon.ts` had already been
  // commented out, so nothing has read this key in a long time. The dead
  // builders and the commented-out flag read are gone with it. Archive 199287.
  EbaySearch = 'ebay-search',
  TwoPhaseLoading = 'two-phase-loading', // Quick internal results + full external results
  ResearchChat = 'research-chat', // Enable chat interface for research tasks
  ResearchInlineChat = 'research-inline-chat', // A/B test: unified single-column chat layout for research
  AmpProductAds = 'amp-product-ads', // Sponsored product ads via adMarketplace
  /** Renders a small green "$" badge on Amazon match cards whose link was
   *  swapped to an AMP click_url (monetized:'amp'). Internal-users-only via
   *  PostHog flag targeting — used to visually confirm which Amazon rows are
   *  earning AMP revenue during rollout. */
  AmpAmazonMonetizedBadge = 'amp-amazon-monetized-badge',
  Poshmark = 'poshmark', // Poshmark kill switch — disables all Poshmark functionality
  PoshmarkBuyLinks = 'poshmark-buy-links', // Poshmark in research buy links pipeline
  PoshmarkDupes = 'poshmark-dupes', // Poshmark in dupes/search endpoints
  /** DUP-1724: enables the per-host scrape race-deadline raise for slow-but-reliable
   *  hosts (Wayfair/Aritzia). Consumed server-side in `apps/api` parse.ts via
   *  `getFeatureFlagNoExposure` (perHostScrapeDeadline.ts). Global on/off kill switch,
   *  not user-targeted. Off/absent/error → safe default (no raise, worker's 12s env
   *  default). Flip ON in PostHog to activate without a redeploy. */
  ScrapePerHostDeadlineEnabled = 'scrape-per-host-deadline-enabled',
  // RETIRED 2026-09-21: `affiliate-router` (755553) — routed the "Also search"
  // partner from hardcoded Wayfair to category-specific preferred partners.
  // Experiment 382464 (DUP-1553) concluded 2026-07-23 at 0% `control` / 100%
  // `test`, shipped observationally, so the category-aware router already
  // served everyone. #5170 made `useAffiliateRouterArm` return the winning arm
  // unconditionally — no PostHog read, no bootstrap-cookie fallback, no
  // readiness wait. Archived 755553 (DUP-2462).
  /**
   * DUP-1920 — A/B experiment: send a bucketed slice of toggler furniture/home
   * traffic to **Ashley Furniture** instead of Wayfair, to find out what an
   * Ashley *broadcast* click earns. Every Ashley number we have came from
   * product-page clicks out of our own grid; on Wayfair that surface differs
   * ~2.6x per confirmed click, so this is a measurement, not an optimisation.
   *
   * Client-evaluated (same pattern as `AffiliateRouter`): `test` → Ashley is
   * enabled in the `furniture_home` lane for that user, where its `enforce`
   * eligibility rule claims its three earning categories (bedroom storage /
   * occasional tables / sofas) and Wayfair keeps the rest; `control`/absent →
   * Wayfair everywhere, byte-identical to today. **This flag's rollout % is the
   * ONLY volume dial and the kill switch** — the router is config-in-code, so
   * `enabled: false` in `affiliateRouter.ts` needs a deploy while this does not.
   * Set it to 0% to stop instantly.
   *
   * ⚠️ Bucketing is PER USER, not per query, and deliberately: a within-user
   * split would leave treatment and control nearly identical and the experiment
   * with almost no power. A treated user gets Ashley on its targeted queries and
   * Wayfair on the rest.
   *
   * ⚠️ **The rollout % is NOT Ashley's share of furniture.** Two filters
   * compose: `rollout% × ~32% of furniture queries matching the rule`. Ramp
   * target is **15%** (Ramin, 2026-08-07) ⇒ ~4.8% of furniture clicks, which is
   * roughly the volume DUP-1927's v2 spec models for Ashley. 5% would deliver
   * ~1.6% and underpower the readout. Widening the regex means lowering this %.
   *
   * ⚠️ Read out on BOTH affiliate networks. Ashley's second network is invisible
   * to every internal system and a Rakuten-only read understates it ~2x. See
   * `docs/references/retailer-conversion-profiles.md`.
   */
  TogglerAshleyFurniture = 'toggler-ashley-furniture',
  /**
   * Toggler config store, Phase 2 step 5 — KILL SWITCH for server-side "Also
   * search" routing. Boolean. `true` → a router-arm broadcast asks
   * `/api/redirect` to DECIDE the retailer (`rr=1`, config read from the
   * database) instead of sending a finished URL built from the config compiled
   * into the browser bundle. Off / absent / not yet resolved → today's
   * client-decided path, byte-identical. Set to off to stop instantly; no
   * deploy needed. The control / router-disabled path never consults it.
   * Read by `useTogglerServerRouting` (web). See
   * `docs/features/active/toggler-config-store/TODO.md` § Phase 2 step 5.
   */
  TogglerServerRouting = 'toggler-server-routing',
  // RETIRED 2026-09-16: `rerank-default-sort` (374206) — concluded LOST
  // 2026-06-08 and pinned to control 100 / test 0, but the flag kept
  // evaluating on every request for three months. Shipping its test arm
  // (default sort = "Best matches") would have cost roughly -$550k/yr on the
  // measured slice. `Recommended` is now the hardcoded default in
  // `useSortCriteriaState`, and the `X-Rerank-Sort-Variant` header is gone.
  // Archive the PostHog flag.
  // RETIRED 2026-09-18: `sponsored-picks-rail` (v1, 708924) — the follow-up to
  // RerankDefaultSort, which rendered the picks as a top-of-page "Sponsored"
  // carousel rail on the legacy `/[hash]` page. PostHog has it collapsed to
  // 100% `control` / 0% `test`, i.e. the rail arm serves nobody, so the
  // `SponsoredPicksRail` component and its branch in `pages/[hash]/index.tsx`
  // are deleted and the page always renders the vertical `OurPicks` list.
  // Archive 708924. See docs/features/active/sponsored-picks-rail/.
  // RETIRED 2026-09-16: `sponsored-picks-rail-v2` (379446) — concluded LOST
  // 2026-07-15, but the flag never stopped serving: ~260k people/month were
  // still being split across a known-losing arm two months later. Fresh data
  // reproduced the verdict (pick clicks -6.3%, p<1e-7) and killed the stored
  // conclusion's "rebuild desktop-only" plan (desktop -2.5%, commission/user
  // -7.4%). The chat rail component and its grid subtraction are deleted.
  // (The v1 flag noted above was a DIFFERENT flag and was still live when this
  // note was written; it has since been retired too. Archive 733249 as well.)
  // `marketplaces-rail` (836692, stacked store cards) and `wayfair-row-stacks`
  // (957927, Wayfair row + stacks) both ended at control for everyone: stacks
  // on 2026-10-13 at −26.9% revenue per shopper, the Wayfair row on 2026-10-21.
  // Their code is deleted. Archive both flags.
  /** Shadow-mode: compute visualScore per match on /api/dupes, log distribution, don't change sort. Phase 2 of visual-match-quality. */
  VisualScoreScoring = 'visual-score-scoring',
  /** Enforce visualScore threshold in sortVisualMatches tier logic. Phase 4 of visual-match-quality. Don't flip on without Phase 3 threshold calibration. */
  VisualScoreSortGate = 'visual-score-sort-gate',
  /**
   * Server-side default for the /api/dupes price-tier filter on multimodal
   * find-similar queries. When on for a user, `priceFilterMode` defaults to
   * 'strict' (band [0.3x, 2.5x] of seed price) for premium seeds (>$30).
   * Client can override by explicitly passing `priceFilterMode`.
   * Validated lift: +38pp multimodal win-rate in milkshake-ph eval v1.
   * See apps/api/src/lib/dupes/priceTierFilter.ts.
   */
  DupesPriceTierFilter = 'dupes-price-tier-filter',
  /**
   * Run image classification inline on `/api/dupes` for fresh image searches
   * where the caller didn't pass `productCategory` and no prior scrape exists.
   * Result populates `filterByCategoryMatch` so cross-category drift (kitchen
   * timer for a watch seed, baseball cleat for a trail sneaker, etc.) is
   * dropped before sort. Adds ~500-1500ms to first-time image searches;
   * rollout-time classifications stay request-local so flagged traffic cannot
   * affect unflagged shared-cache behavior. See
   * apps/api/src/lib/dupes/inlineClassification.ts.
   */
  DupesInlineClassification = 'dupes-inline-classification',
  /** A/B experiment: data-driven Beta-posterior CTR scores vs manual tier sort for Recommended. `test` = score-based, `control` = tier-only. See DUP-589. */
  DupeRankingV2ScoreSort = 'dupe-ranking-v2-score-sort',
  /** Grid revenue-per-tile reranker (shadow-first). Variants: `control` (off), `shadow` (compute + log counterfactual, serve live order), `active` (serve reranked). See docs/features/done/grid-revenue-rerank. */
  DupeGridRevenueRerank = 'dupe-grid-revenue-rerank',
  // RETIRED 2026-09-16: `non-furniture-relevance-sort` — concluded WON
  // 2026-07-15 (+4.2% relative CTR) and shipped to 100% in code. Non-revenue
  // categories now always default to RrfRecommended via
  // `resolveRelevanceDefaultSort`, and the masonry grid rides the same
  // server-reported eligibility. Archive the PostHog flag.
  /**
   * A/B: on rug-intent searches only (query text contains rug/doormat/kilim),
   * promote the rug specialist (Rugs USA) above the tier-1 generalist (Wayfair)
   * as the top "Our Pick". `test` = specialist promoted, `control` = today's
   * behavior. Fires only when the query reads as a rug, so the vast bulk of
   * Wayfair revenue is untouched. Arm is decided in the browser (posthog-js,
   * `useOurPicksRugVariant`) and forwarded on the `x-ourpicks-rug-variant`
   * header; apps/api consumes it (processOurPicksDb.promoteRugSpecialistToFront).
   * Fast readout = per-arm pick CTR / click-share on rug searches (weeks);
   * total-revenue non-inferiority is a long-horizon guardrail (rug revenue is
   * ~0.5% of Wayfair, so exposure is small but slow to mature). See
   * docs/features/active/ourpicks-rug-experiment/.
   */
  OurPicksRug = 'ourpicks-rug',
  /**
   * Team-draft interleaving harness on typed text search (DUP-1022). NOT a
   * PostHog Experiment — it is a serve-time behavior gate, evaluated
   * server-side with `getFeatureFlagNoExposure`; the readout is the
   * per-batch win rate over `link_clicks`, not a PostHog metric.
   *
   * First use is A-vs-degraded-A calibration (arm B = the live list
   * reversed), per condition 3 of
   * `docs/features/active/text-search-retrieval/feasibility-interleaving.md`.
   * Keep it off until that calibration passes; a real two-ranker comparison
   * must not be trusted before the pipeline proves it can detect a
   * known-bad arm. Default off / fail-closed.
   */
  SearchInterleaveCalibrate = 'search-interleave-calibrate',
  /**
   * Partner pin + relevance sort (DUP-2571 → Phase 1). NOT a PostHog Experiment:
   * the SERVER assigns the arm by hashing the temp user id against the flag
   * payload (`lib/experiments/partnerPin.ts`), because PostHog's own bucketing
   * switches arms at sign-in. Read with local evaluation only. Payload:
   * `{ salt, pin2Percent, forceUnits? }`; `pin2Percent: 0` or flag off is the
   * kill switch. See docs/features/active/partner-pin-experiment/.
   */
  PartnerPinRelevance = 'partner-pin-relevance',
  /**
   * Same-shop demotion holdout (DUP-2600 / DUP-2639). NOT a PostHog Experiment:
   * the SERVER assigns the arm by hashing the temp user id against the flag
   * payload (`lib/experiments/sameShopHoldout.ts`), same reason as
   * `PartnerPinRelevance`. Read with local evaluation only, and only when
   * `SAME_SHOP_DEMOTE_MODE=experiment`. Payload: `{ salt, testPercent,
   * forceUnits? }`; `testPercent: 0` or flag off puts everyone in control.
   */
  SameShopDemoteHoldout = 'same-shop-demote-holdout',
  /**
   * Jev photo-search re-rank A/B. NOT a PostHog Experiment: the SERVER assigns
   * the arm with the partner-pin hash (`lib/experiments/jevPhotoRerank.ts`).
   * Local evaluation only. Payload: `{ salt, testPercent, serve, forceUnits? }`.
   * `serve: false` is shadow: the test arm is scored and logged but served
   * today's order. Flag off or `testPercent: 0` puts everyone in control.
   */
  JevPhotoRerank = 'jev-photo-rerank',
  /**
   * Own-catalog text search with the LLM re-ranker (DUP-1843). A serve-time
   * behaviour gate, not a PostHog Experiment: when on, typed FURNITURE/HOME
   * searches also build a second result list from our own catalog — a depth-100
   * hybrid pool re-ranked by Gemini 3 Flash — and hand it to the team-draft
   * interleaving harness as arm B against the live Google-backed list.
   *
   * Gated on furniture because that is where it was measured (~54% of typed
   * volume); beauty's gap is a supply problem a re-ranker cannot fix. Default
   * off / fail-closed: an unknown category, a down embedder, an empty pool or
   * any retrieval error all mean "serve the normal result", never a partial
   * catalog page. Evaluated server-side with `getFeatureFlagLocalOnly`, so keep
   * the conditions to plain rollout — no cohorts.
   *
   * Relevance only: no partner/tier revenue term. See
   * docs/features/active/text-search-retrieval/serving-plan-llm-rerank.md.
   */
  SearchCatalogRerank = 'search-catalog-rerank',
  /**
   * Jev narrows the catalog lane's ~100 candidates to 30 before the Gemini
   * re-rank (`apps/api/src/lib/catalog-text/jevShortlist.ts`). Offline: 0.733
   * vs 0.717 NDCG@10 and re-rank p90 5.1s → 2.6s — see
   * docs/features/active/text-search-retrieval/findings-jev-rerank-2026-09-25.md.
   *
   * ⛔ Plain rollout percentage only — read with `getFeatureFlagLocalOnly`, same
   * constraint as `SearchCatalogRerank`. Absent or failing = off (today's
   * Gemini-only path). `CATALOG_JEV_SHORTLIST=on|off` overrides it per
   * deployment, which is how a preview is tested.
   */
  SearchCatalogJevShortlist = 'search-catalog-jev-shortlist',
  // RETIRED 2026-10-26: `search-catalog-rerank-flash-lite` (919056) — the A/B
  // of Gemini 3.5 Flash-Lite against Gemini 3 Flash as the catalog re-ranker.
  // Flash-Lite won the re-run and is now `RERANK_MODEL_ID` in llmRerank.ts;
  // `CATALOG_RERANK_MODEL` is the rollback switch.
  // RETIRED 2026-10-08: `brain-image-fast-path` (927546) — the A/B of running
  // the visual search before the model on bare image-upload first turns. Test
  // won on web; the web chat now opts in with a constant header and the flag
  // is no longer read. See apps/api/src/lib/brain/imageFastPath.ts.
  // RETIRED 2026-09-23: `near-dupe-collapse` (904448) — the staff-only canary
  // for near-duplicate collapsing. It served its purpose: staff looked at the
  // collapsed page and the call was that showing a shopper the same product
  // eight times is a BUG, not an experiment arm. The collapse now runs
  // unconditionally in `searchCatalogTextRanked`, and the browser hook, the
  // `x-near-dupe-collapse` header and the whole brain plumbing that carried a
  // per-shopper boolean are deleted with it. Archive 904448.
  /**
   * Kill switch for near-duplicate collapsing in the own-catalog lane.
   *
   * ⚠️ **Inverted on purpose: `true` DISABLES.** The collapse is a bug fix that
   * runs for everyone, so every failure mode has to leave it ON. Read strictly
   * as `=== true`; an absent flag, a missing `distinctId`, a PostHog error and
   * a local-eval cache miss must all mean "keep collapsing". A flag whose
   * failure mode is "silently start showing duplicates again" would be the
   * same class of bug as the one it guards.
   *
   * ⛔ **Conditions must stay a plain rollout percentage — no cohorts, no
   * person properties.** This is read server-side with
   * `getFeatureFlagLocalOnly`, which sees only the `distinctId` and never
   * stored person properties, so a property-targeted condition resolves to its
   * default for every caller. Here that default is "not disabled", so the
   * failure is quiet rather than dangerous — but it would still mean a kill
   * switch that does not kill. Same constraint as `SearchCatalogRerank`.
   *
   * Default absent = collapse ON. Nothing to create in PostHog until the day
   * someone needs to turn it off without a deploy.
   */
  NearDupeCollapseDisable = 'near-dupe-collapse-disable',
  /**
   * Dupe-intent search (docs/features/active/dupe-intent-search). When on, a typed query that
   * carries a substitution marker ("X dupe", "cheaper version of X", "something like X") is first
   * resolved by one ungrounded fast-model call into the product the shopper is anchoring ON, plus
   * named alternatives to search FOR — because keyword retrieval on "X dupe" can only return X
   * itself and listings that name-drop it, while the good answers share none of the query's words.
   *
   * Measured need, n=15, run `v3lab-20260903T224026Z`: the anchor product appeared in 12/12
   * grid-bearing threads and was identified in 0/15, across two prompts and three models.
   *
   * Default off / fail-closed: no flag, a timeout, an unparseable answer or no resolved anchor all
   * mean the turn proceeds exactly as it does today. Evaluated server-side with
   * `getFeatureFlagLocalOnly` at the point of use, so keep the conditions to plain rollout — no
   * cohorts. Bucketed on `authUserId ?? tempUserId` so anonymous shoppers are enrolled too — the
   * brand-fan-out gate this feature replaces reads `authUserId` alone and so is never evaluated
   * for anonymous shoppers, one of the three locks that left `discoverBrands` dead in practice.
   *
   * Created in PostHog 2026-09-03 as flag 865556, active at 0% (inert). Ramp by moving the
   * percentage — no deploy needed either way.
   */
  SearchDupeIntent = 'search-dupe-intent',
  /**
   * Kill switch for the marqo catalog-search cohort's use of the engine-keyed
   * `visual_matches` cache (DUP-2452). Plain boolean gate, evaluated LOCAL-ONLY
   * server-side with a constant distinct id — no user targeting, so it resolves
   * from the locally polled definition and costs no round-trip. Default ON:
   * an absent flag or an evaluation error leaves the cache on, and only an
   * explicit `false` turns it off. Turning it off sends marqo back to uncached
   * without a deploy, and its rows stop being read or written. v6 caching is
   * unaffected either way.
   */
  DupesMarqoCache = 'dupes-marqo-cache',
  /**
   * DUP-2853 kill switch: catalog search shows a shopper only products from
   * their own country (US = `US` or no country on the index doc). Plain
   * boolean gate, evaluated LOCAL-ONLY server-side with a constant distinct id
   * — no user targeting, free on the hot path. Default OFF while absent, and
   * OFF on any evaluation error. Turning it off restores today's unfiltered
   * search without a deploy (non-US cache rows go back to their unsuffixed keys).
   */
  CatalogCountryFilter = 'catalog-country-filter',
  /** DUP-1143: fail-closed "Our Picks" suppression. When ON, `/api/dupes`
   *  grants NO monetized picks on a search whose source category couldn't be
   *  resolved (classifier timeout + row-consensus miss) — the case where the
   *  cross-category gate fails OPEN and color-matched catalog rows (e.g. a red
   *  Wayfair ice bucket for a red-glasses search) leak in as top picks. Default
   *  OFF; the cost (no picks on uncategorized searches) is measured by the
   *  `dupes.category_resolved` telemetry. Server-side rollout gate (no
   *  exposure), NOT an experiment source. */
  DupesSuppressPicksUnknownCategory = 'dupes-suppress-picks-unknown-category',
  // RETIRED 2026-09-18: `signup-incentive-wall` (724944) — "Sign up incentive
  // test on homepage and results". PostHog has it collapsed to 100% `test` /
  // 0% `control`, so the locked results sign-up wall already showed for every
  // eligible (logged-out + results) shopper and the no-wall arm served nobody.
  // `ResultsSignupWall` now mounts the wall unconditionally. Archive 724944.
  // RETIRED 2026-09-18: `visual-onboarding-variant` (745099) — the results-page
  // post-signup onboarding treatment (brand-led taste flow as `test` vs the
  // written-question `ResultsOnboarding` as `control`). PostHog has it collapsed
  // to 100% `test` / 0% `control` (its first release condition is a 100% "keep
  // only one variant" group, which shadows the older 50% group below it), so the
  // brand flow already served everyone who reached the step. `AuthContent`'s
  // `ResultsOnboardingVariant` now mounts `BrandTasteOnboardingResults`
  // unconditionally, and `ResultsOnboarding` has now been DELETED too (DUP-2460):
  // the dev launcher, the preview route and its stories were the only things
  // still rendering it, and they no longer do. Archive 745099.
  /** Experiment (DUP-1903): where we ask for the Dupe Drop opt-in. Three arms,
   *  new users only, sticky. `control` (60%) = today's flow, opt-in as the last
   *  onboarding step. `onboarding_first` (20%) = opt-in becomes the FIRST
   *  onboarding step, the prominent skip is removed (the modal X becomes the
   *  exit). `account_creation` (20%) = the ask moves onto the sign-up screen and
   *  onboarding drops its opt-in step. Multivariate — read the VARIANT KEY (not
   *  `isTest`). Absent / unresolved → `control` (safe default). Runs in BOTH
   *  onboarding flows (control + brand). */
  OnboardingMarketingOptinPlacement = 'onboarding-marketing-optin-placement',
  /** Rebuilds the SEO product detail page (`/product/{hash}/{slug}`) to render
   *  the v2 brain chat seeded from the product (product card + Our picks +
   *  collapsed results grid + "Ask anything" composer), with the per-product
  /** Exposes the "Personas" submenu inside the chats `+` menu. */
  ChatsV2Personas = 'chats-v2-personas',
  /** Exposes the "Thinking" submenu inside the chats `+` menu. */
  ChatsV2Thinking = 'chats-v2-thinking',
  /** Exposes the brain-model picker pill on the homepage + chats inputs.
   *  Internal eval lever — pick a model per thread to compare. */
  ChatsV2Models = 'chats-v2-models',
  /** Exposes the team-ready subset of the Brain selector pill on the
   *  homepage input (brain-profiles Phase 3). Currently just the Legwork
   *  profile — the one ready for team testing. Target the whole team
   *  (@dupe.com) in PostHog. Successor (with BrainExperimental) to the
   *  retired exp-brainv2 flag (deleted in PostHog on 2026-11-03); the
   *  server enforces canOverrideProfile
   *  (admin/harness) regardless of what the client shows. */
  BrainTeam = 'brain-team',
  /** Exposes the experimental Brain profiles in the selector pill
   *  (Base / Fashion Agent / Opinionated Shopper) — not yet ready for
   *  broad team testing. Admin-/experimenter-targeted. Composes with
   *  BrainTeam: each flag contributes its profiles to the same pill. */
  BrainExperimental = 'brain-experimental',
  // RETIRED 2026-09-21: `chats-v2-show-products-on-ask` (658193) — showed
  // baseline products on turn 1 even when the brain asked a refinement
  // question. Experiment 369616 concluded 2026-05-11 at 100% `control` / 0%
  // `test`, so the parallel raw-query baseline served nobody. #5173 hardcoded
  // `baselineFlagEnabled` (server) and `chatsV2ShowProductsOnAskInTest`
  // (BrainChat) to `false`; the leftover code (the server's baseline search and
  // injection, the `showProductsOnAsk` body field, and BrainChat's first-turn
  // `ProductCarousel` branch) has since been removed. Archived 658193
  // (DUP-2462).
  /** Allows a grounded 2-5 sentence editorial framing beat on URL-paste turns.
   *  Client-evaluated and forwarded in the brain request body; off/absent keeps
   *  the existing post-search prompt and slim tool projection unchanged. */
  ChatsV2UrlEditorial = 'chats-v2-url-editorial',
  /** Inline follow-up chip row above the chats-v2 input. Derived from Google
   *  Shopping filter options returned by the search tool result.
   *  LEGACY: superseded by ChatsV2SmartFollowUps when that flag is on. */
  ChatsV2FollowUpChips = 'chats-v2-follow-up-chips',
  /** Smart follow-up chips: after every product-bearing v2 turn (any
   *  entry point), a cheap Haiku call generates contextual "narrowing"
   *  chips plus context-adaptive deterministic action chips
   *  (deepResearch when a focal product exists, brandScope when a
   *  dominant brand is present, compare with ≥2 results). Chips are
   *  emitted as a `follow-up-chips` SSE event, persisted on the
   *  assistant turn, and routed by `kind` on tap. When ON this supersedes
   *  the legacy `deriveFollowUpChips` (ChatsV2FollowUpChips) path.
   *  See docs/features/done/chat-follow-up-chips/. */
  ChatsV2SmartFollowUps = 'chats-v2-smart-follow-ups',
  /**
   * Reveals the marketplace dropdown on v2 chat product surfaces
   * (`FilterPills` → `ResultSourceDropdown`). When picked, ProductRow
   * re-runs the same query against an external marketplace catalog
   * (initial value: `shopify-gc` = Shopify Global Catalog via UCP MCP)
   * and shows those results in place of the default retrieval pool.
   * 0% rollout at launch; admins / dev override via the standard
   * `?flag=...` URL param. See docs/features/active/shopify-global-catalog/.
   */
  ShopifyGcMarketplaceDropdown = 'shopify-gc-marketplace-dropdown',
  /**
   * Adds the two admin-only entries — "Dupe catalog" and "Federated" — to the
   * results-source dropdown (`FilterPills` → `ResultSourceDropdown`), plus the
   * timing panel underneath it. Picking either fires ONE
   * `/api/search-with-refinements?compareSources=1` request that returns all
   * three orderings, so flipping between them afterwards is pure client state.
   *
   * ⚠️ Release condition MUST be the **Internal Users cohort (126871)**, not an
   * email-domain rule. The client-side `isAdmin` in `useAuthUser` is an email
   * suffix test and is cosmetic; membership of the cohort is the audience this
   * is meant for, and the API independently enforces a verified admin token —
   * a non-admin who forces the flag on gets `compareSources.engaged === false`
   * with `reason: 'not_admin'` and an unchanged page, by design.
   *
   * Off/absent → the two entries never render, no compare request is ever
   * fired, and the grid keeps today's ordering exactly.
   * See docs/features/active/text-search-retrieval/own-catalog-text-search-STATE.md.
   */
  CompareResultSources = 'compare-result-sources',
  /**
   * Gates the desktop homepage quick-search widget (`HomeSearchWidget`) that
   * fades in at the bottom of the page once the hero search scrolls out of
   * view. The widget is a thin "fake" input: on submit it hands the query to
   * the shared `setSearchState({ method: 'queryParam' })` channel, which the
   * still-mounted homepage `DupeInputHomepage` picks up and runs through its
   * real `onSubmit` (URL detection, chat-v2 routing, pixels, analytics). The
   * widget tags its submissions with `source: 'home-search-widget-compact'` so the
   * downstream analytics (`SearchTermEntered`, `PerformedDupe`, …) are
   * attributable. Client-evaluated render gate only; off/absent → widget
   * never mounts. Acts as a kill switch + measurement on/off toggle.
   */
  HomeSearchWidget = 'home-search-widget',
  /**
   * Gates the homepage category toggle (`HomeCategoryChips`): Furniture,
   * Fashion and Fragrance under the hero headline, starting on Furniture. The
   * pick sets the rotating headline words and the example searches typed in as
   * the hero input's placeholder (shown only while the input is unfocused).
   * Client render gate only; off/absent → no toggle and the hero is unchanged.
   */
  HomeCategoryChips = 'home-category-chips',
  /**
   * Adds a "Recents" item with a red count badge as the FIRST item in the site
   * header (every page using `LayoutHeader` / `LayoutFullHeader`), shown only
   * when the shopper has recent searches. Tapping it opens the side menu
   * scrolled to its Recents list. While on, the homepage "Keep shopping" row
   * (`HomeRecentActivity`) is hidden, since the nav item replaces it.
   * Consumed in apps/web (`useRecentsNav`, `IndexHome`). Off/absent → today's
   * header and the "Keep shopping" row, unchanged.
   */
  HeaderRecentsNav = 'header-recents-nav',
  /**
   * ⚠️ NOT WIRED UP — reading or toggling this flag does nothing today.
   *
   * It was the original kill switch + flight-window control for the sponsored
   * unit on `/steals` (payload `{ enabled, startDate, endDate }`, PR #4304). PR
   * #4337 moved that gating to the Notion CMS ("Sponsored Steals (2026)" →
   * Flights DB: `Enabled` + `Start`/`End`), and no code path reads this key any
   * more — so to pull a live sponsor, untick `Enabled` in Notion and revalidate
   * `/steals`, do NOT come here.
   *
   * Kept because the CMS spec still plans a flag-based org-wide emergency kill
   * switch layered over Notion (decision D3,
   * docs/features/active/steals-sponsored-cms). If that lands, rename this to a
   * brand-neutral key — the flight is per-brand in Notion, so a per-brand flag
   * name is wrong for a global switch. Runbook + campaign history:
   * docs/references/sponsored-brand-placements.md
   */
  StealsSponsoredAnthropologie = 'steals-sponsored-anthropologie',
  /** DUP-2626: shows "Continue with Facebook" in the sign-in modal and drawer.
   *  Needs the facebook.com provider enabled in Firebase (project carrot-301622)
   *  first, or the button fails with `auth/operation-not-allowed`. Rollout gate
   *  and kill switch — do not read it sticky. */
  MetaAuth = 'meta-auth',
  /** Shop Phase 1 home-page band A/B (`control` | `test`, 50/50). Must be a
   *  PostHog Experiment, not a plain flag, or no exposures are recorded. Its arm
   *  rides buy links as `xa` (see `experimentArms.ts`). Spec:
   *  docs/features/active/dupe-marketplace/technical-spec.md §P0-4. */
  BrowseHomeBand = 'browse-home-band',
  /** Shop-wide holdout (`holdout` 10% | `shop` 90%): holdout visitors never see
   *  any Shop unit, through every phase. Check it before any other Shop flag;
   *  holdout visitors never evaluate `browse-home-band`. Spec §P0-4. */
  BrowseHoldout = 'browse-holdout',
  /** Boolean: the `/shop` pages exist for this visitor. Off (or unset) means
   *  middleware answers `/shop` and `/shop/*` with a 404, in every environment,
   *  so the page stays dark until it launches. Evaluated server-side by the
   *  visitor's id, so a cohort (it targets Internal Users) only matches people
   *  who are signed in. Spec: docs/features/active/dupe-marketplace/
   *  technical-spec.md §P1-7. */
  ShopPage = 'shop-page',
}

/**
 * Catalog vector-search configurations (DUP-570). Each entry pairs a
 * Turbopuffer namespace with the Pumice embedding version it was indexed
 * with and whether result attributes are hydrated from Postgres.
 *
 * Single source of truth shared by:
 *   - apps/api dupes/dupes-quick (via resolveCatalogSearchConfigForRequest,
 *     which always returns marqo)
 *   - apps/api turbopuffer-search controller allowlist
 *   - apps/web admin /admin/puffer-search comparison page (dropdowns)
 *
 * `pumiceVersion` MUST match the version the namespace was indexed with —
 * a mismatch returns garbage (querying a v6-embedded namespace with a v5
 * query vector). `pg` is true for the new `catalog_products_v{5,6}`
 * namespaces (they store only minimal attributes on TP); the legacy
 * namespace stores full attributes inline so it needs no hydration.
 */
/**
 * Structural shape every `CATALOG_SEARCH_CONFIGS` entry must satisfy. Kept
 * deliberately BROAD (`key`/`pumiceVersion` are `string`) so the array can be
 * declared `as const` and the precise unions (`CatalogSearchKey`,
 * `PumiceVersion`) DERIVED from it below. Net effect: adding a new catalog
 * version is a single new array entry — no union to widen, no `if`/`switch`
 * to touch anywhere. Consumers use the derived `CatalogSearchConfig` type,
 * not this shape.
 */
interface CatalogSearchConfigShape {
  /** Stable key for flag/UX routing. */
  key: string
  /** Turbopuffer namespace name. */
  namespace: string
  /** Pumice embedding service used to embed the query vector. */
  pumiceVersion: string
  /**
   * Embedder + retrieval family. `'pumice'` = the Width.ai PUMICE dual-tower via
   * `getTurbopufferVisualMatches`' standard ANN path. `'marqo'` = in-house
   * Marqo-Ecommerce-L (Modal query embedder + over-fetch/exact-α re-rank).
   * Consumers branch on THIS to pick the retrieval path — never on
   * `pumiceVersion` (which is a no-op placeholder for the marqo entry).
   */
  backend: 'pumice' | 'marqo'
  /** Hydrate result attributes from Postgres `catalog_products`. */
  pg: boolean
  /** Human label for the admin comparison dropdowns. */
  label: string
  /**
   * Large-catalog retailers that get their own brand-diversity query lane.
   * The retailer KEYS are namespace-specific: the legacy namespace stores
   * ingestion-suffixed keys (`wayfair_fusion`), the new PG-hydrated
   * namespaces store feed-canonical keys (`wayfair`). Using the wrong set
   * makes the `In` lane match nothing — so it lives on the config, where
   * the version is known. Single source of truth for both the dupes
   * routing AND `getVisualMatches`'s default (legacy entry).
   */
  largeRetailers: readonly string[]
  /** Retailers excluded from results (`NotIn` filter). Namespace-specific keys. */
  excludedRetailers: readonly string[]
  /**
   * Per-row vector-distance confidence gate (ANN/cosine `$dist`, lower = better).
   * Rows with `$dist` above this are dropped in `getVisualMatches` (ANN path
   * only — never BM25, whose `$dist` is a different scale). When set, it becomes
   * the DEFAULT for callers that don't pass `distanceThreshold` explicitly, so
   * the production image/URL-dupe path gets a confidence gate without each
   * controller wiring one. Omit (undefined) to leave the path ungated.
   *
   * v6 (Pumice-v6, 0–1 cosine) is calibrated to 0.37: good text+image matches
   * top out ≤0.351, no-answer/typo/out-of-catalog queries floor at ≥0.389, so
   * 0.37 gates the test-row/junk leak while keeping every real match. Evidence:
   * docs/features/done/text-search-ranking/real-query-eval-findings.md
   * § "v6 vector distance gate — live calibration".
   */
  distanceThreshold?: number
}

/**
 * ⛔ Etsy is deliberately NOT here. Do not "fix" this by adding it back.
 *
 * This list carried `'etsy'` for a long time and it matched **nothing** —
 * `catalog_products` stores Etsy under `etsy_rakuten` (4.9M rows; the
 * feed-canonical key keeps its network suffix, unlike its siblings), and the
 * list reaches Turbopuffer as a raw exact-match filter with no aliasing, so a
 * wrong key fails silently as an empty filter rather than an error. That is how
 * it survived (DUP-2047).
 *
 * Removing the dead string is behaviour-neutral by construction — a key that
 * matches 0 rows contributes nothing to either the `In` or the `NotIn` filter.
 * Confirmed empirically before shipping: a 15-seed sweep of this list against
 * `[...this list, 'etsy']` came back identical on every seed — same page fill,
 * same store count, zero net slot change for any retailer.
 *
 * Promoting Etsy to `etsy_rakuten` was measured over four 35–36 seed sweeps and
 * **not** adopted. It showed no page-fill benefit (flat in every run) and the
 * short-page harm that motivated it was unreadable at that sample size. It does
 * buy ~+1.3 retailers per page; if someone wants that trade, make it a
 * deliberate ranking change with its own readout — not a typo fix. Same applies
 * to `homedepot`, which `catalogs_config` sizes as `medium`, not `big`.
 * Findings: `docs/references/large-retailer-lane-measurement.md`.
 */
const FEED_CANONICAL_LARGE_RETAILERS = [
  'wayfair',
  'shein',
  'walmart',
  'homedepot',
] as const

// Retired/unconfigured catalog feeds must not surface while their index rows
// await cleanup (DUP-2503). Keys are exact index retailer values. Do not block
// anthropologie (the real feed) or gap (which still has valid catalog rows).
// Shared with the serving filter so cached results are excluded too.
export const DEFAULT_EXCLUDED_RETAILERS: readonly string[] = [
  'jcpenney',
  'banana_republic',
  'anthropologie_test',
  'j.crew',
  'ikea',
  'eddie_bauer',
  'gap_factory',
  'hugo_boss',
  // International feeds loaded once and switched off (DUP-3157).
  'anker_uk_anker_com',
  'anker_eu_anker_com',
  'bluettipower_bluettipower_co_uk',
  'blochworld_uk_blochworld_com',
  'beginningboutique',
  'beginningboutique_www_beginningboutique_co_nz',
  // Small feeds with no revenue and almost no clicks, offboarded (DUP-3038).
  'happsy',
  'sockwell',
  'jurlique',
  'powerstep',
  'callaway',
  'bali_bras',
  'rafaella',
  'spanx',
  'palladium_boots',
  'earth_shoes',
  'p.j._salvage',
  'ecko_unltd.',
  'ecko',
  'adoreme',
  'mooseknuckles',
  'dancewear_solutions',
  'momcozy',
  'ecco',
]

export const CATALOG_SEARCH_CONFIGS = [
  {
    key: 'v5',
    namespace: 'catalog_products_v5',
    pumiceVersion: 'v5',
    backend: 'pumice',
    pg: true,
    label: 'catalog_products_v5 (new + PG)',
    largeRetailers: FEED_CANONICAL_LARGE_RETAILERS,
    excludedRetailers: DEFAULT_EXCLUDED_RETAILERS,
  },
  {
    key: 'v6',
    namespace: 'catalog_products_v6',
    pumiceVersion: 'v6',
    backend: 'pumice',
    pg: true,
    label: 'catalog_products_v6 (new + PG)',
    largeRetailers: FEED_CANONICAL_LARGE_RETAILERS,
    excludedRetailers: DEFAULT_EXCLUDED_RETAILERS,
    // Confidence gate for the live image/URL-dupe vector path — see field doc
    // on CatalogSearchConfigShape.distanceThreshold for the calibration.
    distanceThreshold: 0.37,
  },
  {
    key: 'marqo',
    namespace: 'catalog_products_marqo_l_cmp',
    // Placeholder — the marqo path embeds with the Modal Marqo-L query embedder,
    // NOT a PUMICE service, so this is never read (dispatch keys off `backend`).
    pumiceVersion: 'v6',
    backend: 'marqo',
    // Display fields come from PG, always: `getMarqoVisualMatches` hydrates
    // unconditionally and the index carries none since DUP-2555. (Read only as
    // the `pgHydrate` log field — the marqo backend has its own hydration.)
    pg: true,
    label: 'catalog_products_marqo_l_cmp (Marqo-Ecommerce-L)',
    largeRetailers: FEED_CANONICAL_LARGE_RETAILERS,
    excludedRetailers: DEFAULT_EXCLUDED_RETAILERS,
  },
  // Test config for the catalog_products_test namespace
  // This config is used to test the catalog_products_test namespace
  // It is used to test specific features or behaviors that are not covered by the other configs
  {
    key: 'test',
    namespace: 'catalog_products_test',
    pumiceVersion: 'v6',
    backend: 'pumice',
    pg: true,
    label: 'catalog_products_test (test + PG)',
    largeRetailers: FEED_CANONICAL_LARGE_RETAILERS,
    excludedRetailers: DEFAULT_EXCLUDED_RETAILERS,
    distanceThreshold: 0.37,
  },
] as const satisfies readonly CatalogSearchConfigShape[]

/** Stable routing key of a catalog-search config (derived from the array). */
export type CatalogSearchKey = (typeof CATALOG_SEARCH_CONFIGS)[number]['key']

/**
 * Pumice embedding-service version (derived from the array). Picks which
 * `PUMICE_<VERSION>_URL` env var supplies the embedding service base URL —
 * see `getPumiceConfig` in apps/api turbopuffer/embedding.ts.
 */
export type PumiceVersion =
  (typeof CATALOG_SEARCH_CONFIGS)[number]['pumiceVersion']

/** A single catalog-search config (narrow union, derived from the array). */
export type CatalogSearchConfig = (typeof CATALOG_SEARCH_CONFIGS)[number]

/**
 * The Pumice v6 config. It is NOT used for serving: /api/dupes, /api/dupes/quick
 * and brain run on `CATALOG_SEARCH_CONFIG_MARQO` only, and the v6 index no
 * longer receives new products. It stays defined for admin/bench tooling
 * (including the `getVisualMatches` default those benches rely on) and the v6
 * maintenance pipeline. Do NOT point it at marqo: callers that pass it embed
 * with Pumice.
 *
 * Resolved by key (not array index) so reordering CATALOG_SEARCH_CONFIGS can't
 * silently repoint it; the throw fails fast at module load if the key is gone.
 */
const activeCatalogConfig = CATALOG_SEARCH_CONFIGS.find((c) => c.key === 'v6')
if (!activeCatalogConfig) {
  throw new Error('Missing catalog config: v6')
}
export const CATALOG_SEARCH_CONFIG_ACTIVE = activeCatalogConfig
/**
 * @deprecated The decision-less default was removed — the resolver now always
 * returns the active config. This alias points at `CATALOG_SEARCH_CONFIG_ACTIVE`
 * for one release cycle so any straggler import keeps compiling; use
 * `CATALOG_SEARCH_CONFIG_ACTIVE` directly. Safe to delete next cycle (this is a
 * private workspace package — no external consumers).
 */
export const CATALOG_SEARCH_CONFIG_DEFAULT = CATALOG_SEARCH_CONFIG_ACTIVE
/**
 * In-house Marqo-Ecommerce-L config (the PUMICE/Width replacement) and the
 * default for every dupes request (see `resolveCatalogSearchConfigForRequest`).
 * The marqo retrieval path lives in `getTurbopufferVisualMatches`; an
 * embedder/search error rejects the catalog leg, with no PUMICE fallback.
 */
const marqoCatalogConfig = CATALOG_SEARCH_CONFIGS.find((c) => c.key === 'marqo')
if (!marqoCatalogConfig) {
  throw new Error('Missing catalog config: marqo')
}
export const CATALOG_SEARCH_CONFIG_MARQO = marqoCatalogConfig

/**
 * Keys of catalog-search configs that no longer exist but still appear as the
 * `csv` attribution param on old click-out links. The redirect allowlist
 * accepts them so those clicks keep their attribution.
 */
export const RETIRED_CATALOG_SEARCH_KEYS = [
  'marqo_fallback',
  'pumice_uncached',
] as const

/**
 * Catalog text-search retailer segmentation (Phase 0). See
 * docs/adr/0005-catalog-text-search-retailer-segmentation.md.
 *
 * Each catalog retailer maps to a SEGMENT that governs where its products
 * surface in catalog text search (`/admin/puffer-search` text mode, over the
 * `catalog_text_full` namespace):
 *   - absent ⇒ 'main'  — quality-new, the default for any unclassified retailer
 *   - 'secondhand'      — resale marketplaces; hidden from main, re-surfaced in
 *                         a separate "Used / secondhand" section
 *   - 'excluded'        — cheap-new/dropship; suppressed from results entirely
 *
 * Assignments are by Strackr revenue-per-click, NOT by perceived "cheapness"
 * (see ADR 0005 for why Poshmark is sectioned while Hello Molly stays in main).
 *
 * KEYS MUST be the EXACT `retailer` attribute values stored in
 * `catalog_text_full` (verified 2026-06-10), which are ingestion-suffixed and
 * inconsistent — NOT the bare feed codes. A wrong key silently matches nothing.
 */
export type RetailerSegment = 'secondhand' | 'excluded'

// Value type includes `undefined` so a lookup of an unlisted retailer is typed
// `RetailerSegment | undefined` — consumers must treat unknown retailers as
// unsegmented, not always-segmented (DUP-1733: asos is no longer a key).
export const RETAILER_SEGMENTS: Record<string, RetailerSegment | undefined> = {
  shein_fusion: 'excluded', // #1 flooder in this namespace; $0.015/click
  // asos removed (DUP-1733): no longer excluded wholesale anywhere — its
  // placeholder-image rows are dropped per-product by `isPlaceholderImageUrl`.
  poshmark_inc_fusion: 'secondhand', // unmonetized C2C resale; sectioned, not deleted
  // aliexpress not in the namespace (2026-05-11 snapshot); re-add with its
  // verified code if/when ingested. aeropostale_com_fusion / maurices_fusion
  // are budget but tiny — left in main for now.
}

export const EXCLUDED_TEXT_RETAILERS: string[] = Object.entries(
  RETAILER_SEGMENTS
)
  .filter(([, s]) => s === 'excluded')
  .map(([r]) => r)

export const SECONDHAND_TEXT_RETAILERS: string[] = Object.entries(
  RETAILER_SEGMENTS
)
  .filter(([, s]) => s === 'secondhand')
  .map(([r]) => r)

/**
 * Retailers hidden from MAIN catalog text search: both excluded and secondhand
 * (secondhand re-surfaces in its own section via SECONDHAND_TEXT_RETAILERS).
 */
export const MAIN_TEXT_EXCLUDED_RETAILERS: string[] = [
  ...EXCLUDED_TEXT_RETAILERS,
  ...SECONDHAND_TEXT_RETAILERS,
]

/** Max results per retailer in main catalog text search (diversity cap). */
export const TEXT_MAX_PER_RETAILER = 6

/**
 * Phase-0 hard-exclude for catalog text search on the LIVE `catalog_products_v6`
 * namespace. These are DROPSHIP / SECONDHAND retailers that flood branded and
 * broad text queries (a SHEIN "BALANCE" tee outranking real New Balance; an
 * all-Poshmark page for "men's blue dress shirt"). Excluded, not demoted.
 *
 * KEYS ARE BARE v6 feed codes (`shein`, `poshmark`) — NOT the ingestion-suffixed
 * keys in `RETAILER_SEGMENTS` (`shein_fusion`), which target the frozen
 * `catalog_text_full` namespace and silently match NOTHING on v6. Both
 * verified live-present in v6 (2026-06-10); the other obvious dropship names
 * (temu/aliexpress/dhgate/wish) return 0 rows there and are omitted as no-ops.
 * Urban Outfitters is a legitimate retailer and is deliberately NOT excluded.
 *
 * No retailer is excluded wholesale for placeholder reasons anymore (DUP-1733):
 * Placeholder-image rows are dropped per-product via `isPlaceholderImageUrl`
 * at serve time. shein/poshmark stay
 * here because they flood text search, NOT because of placeholders.
 *
 * Applied today as the default in the admin search-bench / `/admin/puffer-search`
 * v6 text backends; promote to the live `getVisualMatches` v6 default as a
 * separate, deliberate step (do not edit the v6 config's `excludedRetailers`
 * inline — that is the no-flag prod path and ships immediately).
 */
export const V6_TEXT_EXCLUDED_RETAILERS: string[] = ['shein', 'poshmark']

// The placeholder-image gate (hosts + broken-retailer list + the /steals
// switches) moved to `@dupe/common/placeholderImage` in DUP-1802 and is now
// loaded from the `catalog.placeholder-gate` configs row rather than
// hardcoded here. Build one with `makePlaceholderGate`, or in apps/api call
// `getPlaceholderGate()` (`~/src/lib/placeholder-gate`), which reads the row
// once per process and falls back to the baked-in defaults.

/**
 * Constant stamped on every `Result Viewed` impression so Phase 2 ranker
 * launches can correlate impression/click rates pre- and post-launch.
 * Bump when the organic ranker changes materially. See DUP-591 item #8.
 */
export const RANKER_VERSION = 'v0-legacy-tier'

// Old link - https://calendly.com/dupedotcom/dupe-trade-program-onboarding
export const DUPE_TRADE_SCHEDULE_CALL_URL =
  'https://calendly.com/d/cqwq-d5f-8gy/dupe-trade-sourcing-chat'

export const MAX_AGE_OF_COOKIE = 60 * 60 * 24 * 365 // one year in seconds

export const WCTRCK_COOKIE_KEY = '_wctrck'
export const GA_CLIENT_ID_COOKIE_KEY = '_ga'
export const GA_GCL_AW_COOKIE_KEY = '__gcl_aw'

export const TEMP_USER_ID_KEY = '__dupe_temp_user_id'

/**
 * The `code` on a 401 that means "refresh the Firebase ID token and retry
 * once" (api `rejectUnverifiedToken`, `mobileChatAuthGate`,
 * `/api/create-custom-token`; web `fetchWithFreshIdToken`). The mobile apps
 * read it at the top level of a flat `{ message, code }` body: never rename it.
 */
export const ID_TOKEN_EXPIRED_CODE = 'id_token_expired'

/**
 * Request header (`'1'`) by which a client says it handles that 401 by
 * refreshing and retrying, so the api may send it. Lower case: Express reads
 * headers lower-cased.
 */
export const ID_TOKEN_RETRY_HEADER = 'x-id-token-retry'
export const DUPE_AUTH_USER_ID_KEY = '__dupe_auth_user_id'
// DUCI marketing-attribution session cookie (see docs/references/duci-tracking.md).
// Set by the web middleware; read by apps/api /api/redirect to stamp the active
// session onto link_clicks for click-level campaign attribution.
export const DUCI_SESSION_COOKIE_KEY = '__dupe_duci_session'

// The cookie is client-supplied, so both the web middleware (which mints it and
// keys `marketing_duci_sessions.session_id` off it) and /api/redirect (which
// stamps it into link_clicks.metadata) must check it's well-formed — a
// canonical UUID, the only shape we ever mint — before trusting it. This is a
// shape check, not attestation: it rejects forged/oversized/malformed values
// that would otherwise blow the session_id unique btree index or pollute the
// join, but can't prove we issued this particular UUID. Single source so the
// two call sites can't drift.
const CANONICAL_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Shape check for any client-supplied value we mint with `randomUUID()`.
 * Rejects forged/oversized/malformed strings before they reach an index or a
 * join key; it cannot prove we issued this particular UUID.
 */
export function isCanonicalUuid(
  value: string | undefined | null
): value is string {
  return typeof value === 'string' && CANONICAL_UUID_REGEX.test(value)
}

export function isDuciSessionId(
  value: string | undefined | null
): value is string {
  return isCanonicalUuid(value)
}

export const SELECTED_COUNTRY_KEY = '__dupe_selected_country'
export const DEFAULT_COUNTRY = 'us'
export const DUPE_SUPPORTED_COUNTRIES_MAPPING: CountryCurrencyMapping = {
  us: {
    name: 'US',
    value: 'us',
    flag: '🇺🇸',
    currency_symbols: ['USD', 'US$', '$', 'US'],
  },
  ca: {
    name: 'CA',
    value: 'ca',
    flag: '🇨🇦',
    currency_symbols: ['C', 'CAD', 'C$', 'CAD$', '$'],
  },
  gb: {
    name: 'UK',
    value: 'gb',
    flag: '🇬🇧',
    currency_symbols: ['£', '€', 'GBP'],
  },
  fr: {
    name: 'FR',
    value: 'fr',
    flag: '🇫🇷',
    currency_symbols: ['€', 'EUR', 'EURO'],
  },
  de: {
    name: 'DE',
    value: 'de',
    flag: '🇩🇪',
    currency_symbols: ['€', 'EUR', 'EURO'],
  },
  it: {
    name: 'IT',
    value: 'it',
    flag: '🇮🇹',
    currency_symbols: ['€', 'EUR', 'EURO'],
  },
  nl: {
    name: 'NL',
    value: 'nl',
    flag: '🇳🇱',
    currency_symbols: ['€', 'EUR', 'EURO'],
  },
  es: {
    name: 'ES',
    value: 'es',
    flag: '🇪🇸',
    currency_symbols: ['€', 'EUR', 'EURO'],
  },
  au: {
    name: 'AU',
    value: 'au',
    flag: '🇦🇺',
    currency_symbols: ['A', 'A$', '$', 'AUD'],
  },
  nz: {
    name: 'NZ',
    value: 'nz',
    flag: '🇳🇿',
    currency_symbols: ['N', 'NZD', 'NZ$', '$'],
  },
}

/**
 * Maps country codes to their primary currency code (ISO 4217 standard).
 * Used for resolving ambiguous currency symbols (like $) based on search country context.
 *
 * For supported countries, this is derived from DUPE_SUPPORTED_COUNTRIES_MAPPING.
 * Additional countries using $ symbol are included to prevent misidentification as USD.
 *
 * Note: Only add countries we actively support or that commonly appear in search results.
 */
export const COUNTRY_TO_CURRENCY_CODE: Record<string, string> = {
  // Supported countries (from DUPE_SUPPORTED_COUNTRIES_MAPPING)
  us: 'USD',
  ca: 'CAD',
  gb: 'GBP',
  fr: 'EUR',
  de: 'EUR',
  it: 'EUR',
  nl: 'EUR',
  es: 'EUR',
  au: 'AUD',
  nz: 'NZD',
  // Additional dollar-symbol countries (for future expansion)
  sg: 'SGD', // Singapore
  hk: 'HKD', // Hong Kong
  mx: 'MXN', // Mexico
}

export enum UploadFileTypes {
  IMAGE = 'image',
  DOCUMENT = 'document',
}

export enum MessageTypes {
  PING = 'PING',
  AUTH_SYNC = 'AUTH_SYNC',
  MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
  DUPE_TAB = 'DUPE_TAB',
  CONTEXT_OR_TRAY_ICON = 'CONTEXT_OR_TRAY_ICON',
  ALERT_AM = 'ALERT_AM',
  TRACK_EVENT = 'TRACK_EVENT',
  SIDE_PANEL_OPEN = 'SIDE_PANEL_OPEN',
  IS_SIDEPANEL_OPEN = 'IS_SIDEPANEL_OPEN',
  SIDEPANEL_PDP_STATE = 'SIDEPANEL_PDP_STATE',
  REQUEST_PAGE_METADATA = 'REQUEST_PAGE_METADATA',
  // Background → content: a browser-session stand-down hold was just set for
  // this tab's shop by ANOTHER tab, so re-decide now (DUP-2700).
  STAND_DOWN_HOLD_CHANGED = 'STAND_DOWN_HOLD_CHANGED',
  OPEN_SIDEPANEL = 'OPEN_SIDEPANEL',
  IMAGE_SELECTED = 'IMAGE_SELECTED',
  SHOW_SIDEPANEL = 'SHOW_SIDEPANEL',
  CLOSE_SIDEPANEL = 'CLOSE_SIDEPANEL',
  URL_CHANGE = 'URL_CHANGE',
  RESULT_CLICK = 'RESULT_CLICK',
  SIDEPANEL_CLOSED = 'SIDEPANEL_CLOSED',
  SHOW_IMAGE_PICKER = 'SHOW_IMAGE_PICKER',
  // Side panel → tab (`type`): close the picker overlay if it is open.
  HIDE_IMAGE_PICKER = 'HIDE_IMAGE_PICKER',
  // Side panel → tab (`type`): is the picker script alive here? It answers `true`.
  PING_IMAGE_PICKER = 'PING_IMAGE_PICKER',
  GET_TEMP_USER_ID = 'GET_TEMP_USER_ID',
}

export enum SavedItemSource {
  BOOKMARK = 'bookmark',
}
export const MIGRATE_FORM_URL = 'https://forms.gle/uLA5LUnkB5vnfWh56'
export const MIGRATE_FORM_URL_PREFILL_USERNAME =
  'https://docs.google.com/forms/d/e/1FAIpQLSeKE8baIYxdBhm9ymeEMRn1bUgiz4EEleZCiffv4lVdTAgckA/viewform?entry.1940528030=' // add username at the end

export const EXTENSION_ID = 'pkhpcfoaocmmpmphhenoblggeinedlah' // TODO ensure correct and matches one used on dupe.com

// A Vercel preview build must be told which API it talks to (vercel-deploy.yml
// sets NEXT_PUBLIC_DUPE_API_URL). Falling back to production here is how
// previews of API PRs silently tested — and wrote to — the prod API (DUP-3429).
const resolveDupeApiUrl = (): string => {
  const explicit = process.env.NEXT_PUBLIC_DUPE_API_URL?.trim()
  if (explicit) return explicit
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview') {
    throw new Error(
      'NEXT_PUBLIC_DUPE_API_URL is not set for this preview build. Refusing to fall back to https://api.dupe.com (DUP-3429).'
    )
  }
  return 'https://api.dupe.com'
}
export const DUPE_API_URL = resolveDupeApiUrl()

export const DUPE_API_STAGING_URL = 'https://api-staging.dupe.com'

/**
 * The side panel's hand-over handshake, shared so the two halves cannot drift.
 *
 * The extension puts `?panelHold=product-card` on the deep link it loads in
 * its iframe; the web app reads it and, only then, waits until it has results
 * before telling the panel to reveal them. Absence means "hand over on mount",
 * which is what every build through 0.3.12 already gets.
 *
 * ⚠️⚠️ BOTH apps must agree on BOTH strings, and disagreement is SILENT: the
 * web side treats anything it does not recognise as an old client, so a typo
 * breaks nothing, logs nothing, and simply means the behaviour never happens
 * for anybody. That is why these live here rather than as literals at four
 * call sites across two apps.
 *
 * ⚠️ The value says what the panel is holding, deliberately, rather than `1`.
 * A bare `1` in a URL tells a reader nothing, and `panelHold=product-card`
 * explains the whole handshake to the next person who sees it in a log.
 *
 * ⚠️ The param must survive every redirect between the deep link and the
 * results route — `pages/extension/sidepanel-chat.tsx` (twice) and
 * `middleware.ts`, each of which REBUILDS the query rather than appending to
 * it. The same trap already dropped `ext=1` once.
 */
export const SIDE_PANEL_HOLD_PARAM = 'panelHold'

/**
 * What the panel promises to keep on screen while the results load: a real
 * product card, with the image, title and price it scraped from the shop page.
 * A client that cannot promise that must not send this.
 */
export const SIDE_PANEL_HOLD_VALUE = 'product-card'

// Style options for restyle
export const STYLE_OPTIONS = PROMPTS

// Facebook Pixel ID
export const FB_PIXEL_ID = '1001580568749204'

/**
 * OpenAI (ChatGPT Ads) pixel / data-source ID for `Dupe.com Data Source` on
 * ad account `adacct_6ab5278903a48192823b7e809d69f4c8` (DUP-2570).
 *
 * Public by design — it ships in page source exactly like `FB_PIXEL_ID`, so it
 * is a constant rather than an env var. Changing it silently re-points every
 * ChatGPT conversion at a different data source.
 */
export const OPENAI_PIXEL_ID = 'BGhPhWyP3bzyRY4zHeFhX7'

/**
 * The OpenAI standard events the web pixel fires and the api relays
 * (`apps/web/src/lib/analytics/openAiConversions.ts`, `apps/api/src/controllers/
 * openAiConversionRelay.ts`). A campaign binds to its event name at creation,
 * so renaming one silently detaches a live campaign.
 */
export const OPENAI_SEARCH_EVENT = 'contents_viewed'
export const OPENAI_REGISTRATION_EVENT = 'registration_completed'
export type OpenAiConversionEventType =
  | typeof OPENAI_SEARCH_EVENT
  | typeof OPENAI_REGISTRATION_EVENT

/**
 * An OpenAI ad-click id (`oppref`) we are willing to relay: printable ASCII
 * with no spaces, and short. OpenAI calls it opaque, so nothing stricter. One
 * pattern for both sides, or the api rejects ids the browser sends.
 */
export const OPENAI_CLICK_ID_SHAPE = /^[\x21-\x7E]{1,512}$/

export const DEEP_SEARCH_SUPPORTED_COUNTRIES = ['us', 'ca']

// NOTE: duplicated in dupe-mobile-app repo in src/lib/constants.ts
export enum FilterLabel {
  Department = 'Department',
  Color = 'Color',
  Features = 'Features',
  Fit = 'Fit',
  LegStyle = 'Leg Style',
  Material = 'Material',
  Rise = 'Rise',
  ProductRating = 'Product Rating',
  Price = 'Price',
  Stores = 'Stores',
  ClosureType = 'Closure Type',
  MensSize = "Men's Size",
  WomensSize = "Women's Size",
  Style = 'Style',
  Specialty = 'Specialty',
  FreeShipping = 'Free shipping',
  Brand = 'Brand',
  SortBy = 'Sort by',
}

export type Filter = Partial<Record<FilterLabel, string[]>>

export const FILTER_LABELS_FOR_PROMPTING: FilterLabel[] = [
  FilterLabel.Department,
  FilterLabel.Style,
  FilterLabel.Material,
]

export const FILTER_LABELS_EXCLUSIONS: FilterLabel[] = [
  FilterLabel.Brand,
  FilterLabel.Stores,
  FilterLabel.FreeShipping,
  FilterLabel.SortBy,
  FilterLabel.ProductRating,
]

/**
 * Most photos an admin can pin on a fragrance launch point
 * (`launch_points.images`; the first is the thumbnail). Enforced by the api's
 * PUT …/launch-points/:id/images and mirrored by the admin page.
 */
export const MAX_LAUNCH_POINT_IMAGES = 8

/**
 * Who may see a launch point (`launch_points.visible_to`): its creator, internal
 * users, or anyone. The web admin mirrors these for its selector.
 */
export const LAUNCH_POINT_VISIBLE_TO = [
  'private',
  'internal',
  'public',
] as const

/**
 * What a person can react to (`user_reactions.entity_type`). Ids are each table's own text id:
 * `fragrance_items.id` and `launch_points.id`. `user_collection_items` uses the same types. Add
 * a type here, to both tables' CHECKs, and to the GDPR sweep together.
 */
export const USER_REACTION_ENTITY_TYPES = [
  'fragrance_item',
  'launch_point',
] as const
export type UserReactionEntityType = (typeof USER_REACTION_ENTITY_TYPES)[number]

/**
 * A like, a dislike or a neutral "it's okay" (`user_reactions.reaction`). Dislikes are kept
 * because the scent profile needs what separates them from likes (scent-profile decisions A3),
 * even where the UI only offers a heart. `neutral` is an opinion from someone who has smelled
 * it (owned or tried) and neither loves nor dislikes it.
 */
export const USER_REACTIONS = ['like', 'dislike', 'neutral'] as const
export type UserReaction = (typeof USER_REACTIONS)[number]

/** Where a reaction was made (`user_reactions.source`). */
export const USER_REACTION_SOURCES = [
  'admin_fragrance_bottles',
  'admin_fragrance_bottle_drawer',
  'admin_fragrance_launch_point',
  'admin_fragrance_celebs',
  /** A shopper's heart or Not-for-me in the fragrance chat. */
  'fragrance_chat',
] as const
export type UserReactionSource = (typeof USER_REACTION_SOURCES)[number]

/**
 * The lists a person keeps things on (`user_collection_items.list`): `owned` is what they have,
 * not what they like, so it never counts as taste (fragrance decisions FD-018); `want` is what
 * they want to try, a wish, not taste either; `tried` is smelled but not owned (a sample, a
 * tester), also not taste on its own. A new list is a new value here plus the table's
 * CHECK. Things are `USER_REACTION_ENTITY_TYPES`.
 */
export const USER_COLLECTION_LISTS = ['owned', 'want', 'tried'] as const
export type UserCollectionList = (typeof USER_COLLECTION_LISTS)[number]

/** Where a thing was put on a list (`user_collection_items.source`). */
export const USER_COLLECTION_SOURCES = [
  /** Saved from the fragrance chat, e.g. the my-bottles card. */
  'fragrance_chat',
] as const
export type UserCollectionSource = (typeof USER_COLLECTION_SOURCES)[number]
